import { DateTime } from "luxon";
import DataTableYearly from "@/components/DataTableYearly.vue";
import OutputSection from "@/components/OutputSection.vue";
import OutputSSEMultipleProject from "@/components/OutputSSEMultipleProject.vue";
import OutputSSEToolbar from "@/components/OutputSSEToolbar.vue";
import OutputTimeSeriesToolbar from "@/components/OutputTimeSeriesToolbar.vue";
import Plotly from "@/components/charts/Plotly.vue";
import ChartTimeseries from "@/components/charts/ChartTimeseries.vue";
import ApparentPowerTimeseries from "@/components/charts/ApparentPowerTimeseries.vue";
import {
  parseTimeSeriesResources,
  parseSQLDaily,
  parseSQLMonthly,
  parseSQLYearly,
} from "@/utils/sseQuery";
import { sqlToObject } from "@/mixins/sql-utils";
import initSqlDb from "@/utils/duckdb-client";

export default {
  data() {
    return {
      availableServices: ["EA", "PS", "BS", "MEA", "ESOCM", "RS", "DCM", "DR", "FC", "CP", "RRC"],
      dbSql: null, // DuckDB or sqlite connection

      // set on initial load
      SSEAvailableProjects: [{ name: "Loading...", id: 0 }],
      SSEYearItems: [], // array of numbers
      allYearlyData: {},

      // set on toolbar selections
      SSEYearlyData: {},
      SSETimeseriesData: {},
      SSEDailyData: {},
      SSEMonthlyData: {},

      // set on changing project
      SSEResourcesItems: {
        storages: [],
        renewable_generators: [],
        thermal_generators: [],
        demands: [],
        markets: [],
      },

      SSEOutputLoaded: false,
    };
  },
  methods: {
    // Helper methods for lazy loading
    getDatabaseRef() {
      return this.dbSql;
    },

    async initSSEOutputs() {
      if (this.dbSql) {
        return;
      }
      const output = this.outputs.find((v) => v.type_name === "STACKED_SERVICES_EMULATOR");

      try {
        this.dbSql = await initSqlDb(output);

        await this.getAvailableProjects();
        await Promise.all([
          this.getResourcesForToolbar(),
          this.getYearItems(),
          this.getHasTimeseries(),
          this.getAllYearlyData(),
        ]);

        // Keep database connection open for lazy loading - will be closed in beforeDestroy
      } catch (error) {
        console.error("Error parsing SSE outputs:", error);
      }
    },

    async getHasTimeseries() {
      const dbSql = this.getDatabaseRef();
      if (!dbSql) return;

      try {
        const response = await dbSql.exec(`SELECT * FROM 'markets' LIMIT 1`);
        if (response[0] && response[0].values.length) {
          this.SSEToolbarSettings.hasTimeseries = true;
        } else {
          this.SSEToolbarSettings.hasTimeseries = false;
        }
      } catch (err) {
        console.error("on getHasTimeseries:", err);
        this.SSEToolbarSettings.hasTimeseries = false;
      }
    },

    async getYearItems() {
      const dbSql = this.getDatabaseRef();
      if (!dbSql) return;

      const yearResponse = await dbSql.exec(`SELECT DISTINCT year FROM 'yearly_projects'`);
      if (yearResponse[0]) {
        this.SSEYearItems = yearResponse[0].values.map((row) => row[0]);
        this.SSEToolbarSettings.selectedYear = [this.SSEYearItems[0]];
      }
      const tsResponse = await dbSql.exec(`SELECT MIN(datetime) FROM 'ts_projects'`);
      if (tsResponse[0]) {
        // how romantic
        const firstDate = tsResponse[0].values[0][0].slice(0, 10);
        this.SSEDates = [firstDate, firstDate];
      }
    },

    getSelectedResourcesFromToolbar() {
      const resourceSelections = {};
      const resourceTypes = [
        "markets",
        "storages",
        "thermal_generators",
        "renewable_generators",
        "demands",
      ];

      resourceTypes.forEach((resourceType) => {
        resourceSelections[resourceType] = this.SSEToolbarSettings[resourceType] || [];
      });

      return resourceSelections;
    },

    async updateSSEData() {
      this.SSEOutputLoaded = false;
      const { viewMode } = this.SSEToolbarSettings;
      const dbSql = this.getDatabaseRef();
      if (!dbSql) return;
      const resourceSelections = this.getSelectedResourcesFromToolbar();
      const servicableIDs = Object.values(resourceSelections).flat();

      if (viewMode === "timeSeries") {
        await this.getTimeSeriesData(dbSql, servicableIDs);
      } else if (viewMode === "daily") {
        await this.getDailyData(dbSql, servicableIDs);
      } else if (viewMode === "monthly") {
        await this.getMonthlyData(dbSql, servicableIDs);
      } else if (viewMode === "annual") {
        await this.getYearlyData(dbSql, servicableIDs);
      } else {
        console.error("Invalid view mode:", viewMode);
      }
      this.SSEOutputLoaded = true;
    },

    /**
     * Fill SSEYearlyData.
     * structure is { [year]: { [resourceType]: { [ResourceName]: data obj}}}
     * e.g. { "2021": { storages: { "Storage 1": { id: 1, name: "Storage 1", charge: 1000, ... } } } }
     */
    async getYearlyData(dbSql, servicableIDs) {
      this.SSEYearlyData = await parseSQLYearly(dbSql, servicableIDs);
    },

    /** yearly data for all projects (for multi project view) */
    async getAllYearlyData() {
      const dbSql = this.getDatabaseRef();
      if (!dbSql) return;
      const projectsQ = await dbSql.exec("SELECT id, name FROM projects");
      if (!projectsQ[0]) throw new Error("no projects in output");
      const projects = projectsQ[0].values.map((v) => sqlToObject(projectsQ[0].columns, v));
      const servicablesQ = await dbSql.exec("SELECT id, projectID FROM servicables");
      if (!servicablesQ[0]) throw new Error("no servicables in output");
      const servicables = servicablesQ[0].values.map((v) =>
        sqlToObject(servicablesQ[0].columns, v)
      );
      const allData = {};
      await Promise.all(
        projects.map(async ({ id: projectId, name: projectName }) => {
          const projectServicables = servicables
            .filter((s) => s.projectID === projectId)
            .map((s) => s.id);
          const yearlyData = await parseSQLYearly(dbSql, projectServicables);
          allData[projectName] = yearlyData;
        })
      );
      this.allYearlyData = allData;
    },

    async getTimeSeriesData(dbSql, resourceSelections) {
      if (this.SSEToolbarSettings.viewMode !== "timeSeries") return;

      let projectWithTimeSeries = this.getProjectMetadata();
      if (!projectWithTimeSeries || !dbSql) return;

      try {
        // TODO: handle streaming case (when dates have time component)
        projectWithTimeSeries = await parseTimeSeriesResources(
          projectWithTimeSeries,
          dbSql,
          resourceSelections,
          this.SSEDates
        );

        // Extract time series outputs (exclude metadata)
        const {
          yearlyResults,
          id,
          dailyResults,
          monthlyResults,
          cyclingLimits,
          name,
          powerUnits,
          energyUnits,
          timeUnits,
          ...timeseriesOutputs
        } = projectWithTimeSeries;

        const result = Object.keys(timeseriesOutputs).reduce(
          (acc, resourceType) => ({
            ...acc,
            [resourceType]: Object.entries(timeseriesOutputs[resourceType]).reduce(
              (a, resourceEntry) => ({
                ...a,
                [resourceEntry[0]]: Object.values(resourceEntry[1]).flat(),
              }),
              {}
            ),
          }),
          {}
        );
        this.SSETimeseriesData = result;
      } catch (error) {
        console.error("Error loading time series data:", error);
      }
    },

    // TODO: remove this method
    getProjectMetadata() {
      const projectId = this.SSEToolbarSettings.project;
      return { id: projectId };
    },

    async getDailyData(dbSql, resourceSelections) {
      const projectMetadata = this.getProjectMetadata();
      if (!projectMetadata || !dbSql) return;

      try {
        // Lazy load daily data on-demand
        let projectWithDaily = { ...projectMetadata };
        projectWithDaily = await parseSQLDaily(
          projectWithDaily,
          dbSql,
          resourceSelections,
          this.SSEToolbarSettings.selectedYear
        );

        this.SSEDailyData = projectWithDaily.dailyResults || {};
      } catch (error) {
        console.error("Error loading daily data:", error);
      }
    },

    async getMonthlyData(dbSql, resourceSelections) {
      const projectMetadata = this.getProjectMetadata();
      if (!projectMetadata || !dbSql) return;

      try {
        // Lazy load monthly data on-demand
        let projectWithMonthly = { ...projectMetadata };
        projectWithMonthly = await parseSQLMonthly(
          projectWithMonthly,
          dbSql,
          resourceSelections,
          this.SSEToolbarSettings.selectedYear
        );

        this.SSEMonthlyData = projectWithMonthly.monthlyResults || {};
      } catch (error) {
        console.error("Error loading monthly data:", error);
      }
    },

    async getAvailableProjects() {
      const dbSql = this.getDatabaseRef();
      if (!dbSql) {
        console.error("Database connection is not available");
        return;
      }
      const metadataQuery = await dbSql.exec("SELECT id, name FROM projects");
      if (!metadataQuery[0]) {
        throw new Error("Project metadata not found");
      }
      const projectMetadata = metadataQuery[0].values.map((row) => {
        const { id, name } = sqlToObject(metadataQuery[0].columns, row);
        return { id, name };
      });
      this.SSEAvailableProjects = projectMetadata;
      this.SSEToolbarSettings.project = projectMetadata[0].id;
    },

    async getResourcesForToolbar() {
      const dbSql = this.getDatabaseRef();

      if (!dbSql) {
        console.error("Database connection is not available");
        return;
      }

      const projectId = this.SSEToolbarSettings.project;

      const servicablesMap = {
        storages: [],
        renewable_generators: [],
        thermal_generators: [],
        demands: [],
        markets: [],
      };
      const servicablesQuery = await dbSql.exec(
        `SELECT * FROM 'servicables' WHERE projectID = ${projectId}`
      );
      if (servicablesQuery[0]) {
        const objectKeys = servicablesQuery[0].columns;
        servicablesQuery[0].values.forEach((row) => {
          const rowObject = sqlToObject(objectKeys, row);
          const { id, name, type } = rowObject;
          // Map resource type names to match database table names
          let mappedType = type;
          if (type === "thermalGens") mappedType = "thermal_generators";
          if (type === "renGens") mappedType = "renewable_generators";
          servicablesMap[mappedType].push({
            text: name,
            value: id,
          });
        });
      }
      this.SSEResourcesItems = servicablesMap;
      // default select the first of each type
      Object.keys(servicablesMap).forEach((key) => {
        const firstItem = servicablesMap[key][0];
        if (firstItem) {
          this.SSEToolbarSettings[key] = [firstItem.value];
        }
      });
    },

    getReserveAnnualValue(resource, resourceId, key, reserveName) {
      const yearlyData = this.SSEYearlyData;
      return Object.values(yearlyData)
        .filter((year) => Object.keys(year[resource]).length)
        .map((year) =>
          Object.values(year[resource]).find((r) => r.id === resourceId).R[reserveName]
            ? Object.values(year[resource]).find((r) => r.id === resourceId).R[reserveName][key]
            : 0
        );
    },
    getAnnualValue(resource, resourceId, key, serviceKey = null) {
      const yearlyData = this.SSEYearlyData;
      if (serviceKey) {
        return Object.values(yearlyData)
          .filter((year) => Object.keys(year[resource]).length)
          .map(
            (year) =>
              Object.values(year[resource]).find((storage) => storage.id === resourceId)[key][
                serviceKey
              ]
          );
      }
      return Object.values(yearlyData)
        .filter((year) => Object.keys(year[resource]).length)
        .map(
          (year) =>
            Object.values(year[resource]).find((resourceItem) => resourceItem.id === resourceId)[
              key
            ]
        );
    },
    reservePowerNominationData(dataArray, reserveKey) {
      const charge = this.getReserveChartData(dataArray, reserveKey, "consumption", {
        valueModifier: (v) => v * -1,
      });
      const discharge = this.getReserveChartData(dataArray, reserveKey, "generation");

      return charge.y.every((v) => !Number.isNaN(v)) && discharge.y.every((v) => !Number.isNaN(v))
        ? { x: charge.x, y: charge.y.map((val, i) => val + discharge.y[i]) }
        : { x: [], y: [] };
    },
    powerNominationData(dataArray, service) {
      const charge = this.getChartData(dataArray, "consumption", {
        serviceKey: service,
        valueModifier: (v) => v * -1,
      });
      const discharge = this.getChartData(dataArray, "generation", {
        serviceKey: service,
      });
      return charge.y.every((v) => !Number.isNaN(v)) && discharge.y.every((v) => !Number.isNaN(v))
        ? { x: charge.x, y: charge.y.map((val, i) => val + discharge.y[i]) }
        : { x: [], y: [] };
    },
    getReserveChartData(
      dataArray,
      reserveKey,
      key,
      {
        dateKey = "datetime",
        valueModifier = (v) => v,
        dataType = "timeSeries",
        selectedYear = null,
      } = {}
    ) {
      const data = { x: [], y: [] };
      if (dataType === "timeSeries") {
        const startIndex = dataArray.findIndex((entry) =>
          this.datetimeBetweenDates(entry[dateKey])
        );
        dataArray
          .slice(startIndex, startIndex + this.SSEDailyRecords * this.SSENumberOfDays)
          .forEach((entry) => {
            data.x.push(entry[dateKey]);
            data.y.push(valueModifier(entry.R[reserveKey][key]));
          });
      } else if (selectedYear && ["daily", "monthly"].includes(dataType)) {
        dataArray[selectedYear].forEach((entry) => {
          if (this.SSEToolbarSettings.selectedYear.length >= 2) {
            data.x.push(`2021-${entry[dateKey].slice(entry[dateKey].indexOf("-") + 1)}`);
          } else {
            data.x.push(entry[dateKey]);
          }
          if (reserveKey) {
            data.y.push(valueModifier(entry.R[reserveKey] ? entry.R[reserveKey][key] || 0 : 0));
          } else {
            data.y.push(valueModifier(entry.R[key]));
          }
        });
      }
      return data;
    },
    getProfileChartData(profileId) {
      if (profileId) {
        let profile = this.getProfile(profileId);
        if (!profile) {
          this.findProfile(profileId).then((res) => {
            profile = res;
          });
        }
        console.debug("Profile", profile);
        if (profile) {
          let { value } = profile;
          if (typeof value === "string") {
            value = JSON.parse(value);
          }
          const startIndex = value.findIndex((entry) => this.datetimeBetweenDates(entry.date));
          const dataInterval = DateTime.fromISO(value[1].date, {
            zone: "utc",
          })
            .diff(
              DateTime.fromISO(value[0].date, {
                zone: "utc",
              }),
              "minutes"
            )
            .toObject().minutes;

          const data = { x: [], y: [] };
          value
            .slice(startIndex, startIndex + (1440 / dataInterval) * this.SSENumberOfDays)
            .forEach((entry) => {
              data.x.push(entry.date);
              data.y.push(entry.value);
            });
          return data;
        }
      }
      return {};
    },
    getResourceCompChartData(resource1Id, resource1Key, resource2Key, dataKey) {
      const yearlyData = this.SSEYearlyData;
      const firstYear = Object.keys(yearlyData)[0];
      const selectedResources = [
        {
          resourceName: Object.values(yearlyData[firstYear][resource1Key]).find(
            (v) => v.id === resource1Id
          ).name,
          resourceId: Object.values(yearlyData[firstYear][resource1Key]).find(
            (v) => v.id === resource1Id
          ).id,
          resourceKey: resource1Key,
        },
        ...Object.values(yearlyData[firstYear][resource2Key]).map((el) => ({
          resourceName: el.name,
          resourceId: el.id,
          resourceKey: resource2Key,
        })),
      ];
      const years = Object.keys(yearlyData);
      const data = selectedResources.map((resource) => ({
        x: years,
        y: years.reduce((acc, year) => {
          acc.push(
            Object.values(yearlyData[year][resource.resourceKey]).find(
              (f) => f.id === resource.resourceId
            )
              ? Object.values(yearlyData[year][resource.resourceKey]).find(
                  (f) => f.id === resource.resourceId
                )[dataKey]
              : 0
          );
          return acc;
        }, []),
        name: resource.resourceKey === "generators" ? resource.resourceName : resource.resourceName,
        showlegend: true,
        type: "bar",
      }));
      return data;
    },
    getAnnualChartData(resource, resourceId, key, { serviceKey = null, reserve = null } = {}) {
      const data = { x: [], y: [] };
      const yearlyData = this.SSEYearlyData;
      Object.keys(yearlyData).forEach((year) => {
        if (serviceKey) {
          data.y.push(
            Object.values(yearlyData[year][resource]).find((res) => res.id === resourceId)[
              serviceKey
            ][key]
          );
        } else if (reserve) {
          data.y.push(
            Object.values(yearlyData[year][resource]).find((res) => res.id === resourceId).R[
              reserve
            ]
              ? Object.values(yearlyData[year][resource]).find((res) => res.id === resourceId).R[
                  reserve
                ][key] || 0
              : 0
          );
        } else {
          data.y.push(
            Object.values(yearlyData[year][resource]).find((res) => res.id === resourceId)[key]
          );
        }
        data.x.push(year);
      });
      return data;
    },
    getDailyChartData(
      dataArray,
      key,
      { dateKey = "datetime", valueModifier = (v) => v, serviceKey = null } = {}
    ) {
      const data = { x: [], y: [] };
      Object.values(dataArray).forEach((resourceData) => {
        resourceData.forEach((entry) => {
          data.x.push(entry[dateKey]);
          if (serviceKey) {
            data.y.push(valueModifier(entry[serviceKey] ? entry[serviceKey][key] || 0 : 0));
          } else {
            data.y.push(valueModifier(entry[key]));
          }
        });
      });
      return data;
    },
    getChartData(
      dataArray,
      key,
      {
        dateKey = "datetime",
        valueModifier = (v) => v,
        duplicateLast = false,
        serviceKey = null,
        dataType = "timeSeries",
        selectedYear = null,
        sort = false,
      } = {}
    ) {
      const data = { x: [], y: [] };
      if (dataType === "timeSeries") {
        dataArray.forEach((entry) => {
          data.x.push(entry[dateKey]);
          if (serviceKey) {
            data.y.push(valueModifier(entry[serviceKey] ? entry[serviceKey][key] || 0 : 0));
          } else {
            data.y.push(valueModifier(entry[key]));
          }
        });
        if (duplicateLast) {
          data.x.push(dataArray.at(-1)[dateKey]);
          data.y.push(data.y[data.y.length - 1]);
        }
      } else if (selectedYear && ["daily", "monthly"].includes(dataType)) {
        if (sort) {
          // Sort values for duration curves
          const copyObject = { ...dataArray[selectedYear] };
          Object.values(copyObject)
            .sort((a, b) => (a[key] < b[key] ? 1 : -1))
            .forEach((entry, i) => {
              if (this.SSEToolbarSettings.selectedYear.length >= 2) {
                data.x.push((i / 365) * 100);
              } else if (dataType === "daily") {
                data.x.push(
                  DateTime.fromISO(entry[dateKey], { zone: "UTC" }).toLocaleString(
                    DateTime.DATE_SHORT
                  )
                );
              } else {
                // Monthly
                data.x.push(
                  DateTime.fromISO(entry[dateKey], { zone: "UTC" }).toLocaleString({
                    month: "2-digit",
                    year: "2-digit",
                  })
                );
              }

              if (serviceKey) {
                data.y.push(valueModifier(entry[serviceKey] ? entry[serviceKey][key] || 0 : 0));
              } else {
                data.y.push(valueModifier(entry[key]));
              }
            });
        } else {
          // console.log("selectedyear", serviceKey, key, selectedYear, dataArray[selectedYear]);
          dataArray[selectedYear].forEach((entry) => {
            if (this.SSEToolbarSettings.selectedYear.length >= 2) {
              data.x.push(`2021-${entry[dateKey].slice(entry[dateKey].indexOf("-") + 1)}`);
            } else {
              data.x.push(entry[dateKey]);
            }
            if (serviceKey) {
              data.y.push(valueModifier(entry[serviceKey] ? entry[serviceKey][key] || 0 : 0));
            } else {
              data.y.push(valueModifier(entry[key]));
            }
          });
        }
      }
      return data;
    },
    dailyChartLayout(title) {
      return {
        images: [this.acelerexLogo],
        yaxis: {
          title,
          fixedrange: true,
        },
        xaxis: {
          tickformat: this.SSEToolbarSettings.selectedYear.length >= 2 ? "%d/%m" : "",
        },
        barmode: "relative",
      };
    },
    monthlyChartLayout(title) {
      return {
        ...this.dailyChartLayout(title),
        xaxis: {
          tickformat: this.SSEToolbarSettings.selectedYear.length >= 2 ? "%m" : "",
        },
      };
    },
    mapReserveColor(reserveIndex) {
      const colors = ["#BCE931", "#018F5D", "#CB5D08", "#E8C600", "#7327D3", "#137AF3"];
      return colors[reserveIndex] || "#137AF3";
    },
    mapServiceColor(service) {
      return (
        {
          EA: "#4046CA",
          PS: "#F68511",
          ESOCM: "#DE3D82",
          MEA: "#7E84FA",
          BS: "#72E06A",
          RS: "#137AF3",
          FC: "#0CB5AE",
          CP: "#7325D3",
        }[service.includes("_") ? service.slice(0, service.indexOf("_")) : service] || "#0CB5AE"
      );
    },
    mapServiceToOutputKey(service) {
      return {
        energyArbitrage: "EA",
        peakShavingGrid: "PS",
        blackService: "BS",
        ESOCManagement: "ESOCM",
        marketEnergyArbitrage: "MEA",
        renewableEconomicShifting: "RS",
        demandChargeManagement: "DCM",
        demandResponse: "DR",
        flickerControl: "FC",
        cleanPeak: "CP",
        rampRateControl: "RRC",
      }[service];
    },
    mapServiceKey(service) {
      return (
        `${
          {
            EA: "Energy Arbitrage",
            PS: "Peak Shaving",
            BS: "Black Start",
            MEA: "Market Energy Arbitrage",
            ESOCM: "SoC Management",
            RS: "Renewable Economic Shifting",
            DCM: "Demand Charge Management",
            energy: "Energy",
            rps: "Renewable Peak Shaving",
            DR: "Demand Response",
            FC: "Flicker Control",
            CP: "Clean Peak",
            RRC: "Ramp Rate Control",
          }[service.includes("_") ? service.slice(0, service.indexOf("_")) : service]
        }${
          service.includes("_")
            ? this.snakeToTitleCase(service.slice(service.indexOf("_") + 1))
            : ""
        }` || service
      );
    },
  },
  computed: {
    SSEActiveProjectInput() {
      return this.input.projects.find((p) => p.id === this.SSEToolbarSettings.project);
    },
    SSEOutput() {
      const page = {
        title: "Stacked Services Emulator",
        component: OutputSSEToolbar,
        props: { settings: this.SSEDashboardToolbarSettings },
        sections: [],
      };
      if (this.SSEDashboardToolbarSettings.viewMode === "single") {
        const timeSeriesToolbar = {
          component: OutputTimeSeriesToolbar,
          props: {
            page: "sse",
            settings: this.SSEToolbarSettings,
            dates: this.SSEDates,
            projectItems: this.SSEAvailableProjects.map((p) => ({
              text: p.name,
              value: p.id,
            })),
            resourcesItems: this.SSEResourcesItems,
            yearItems: this.SSEYearItems,
          },
          cardProps: {
            flat: true,
            outlined: true,
          },
        };
        page.sections.push(timeSeriesToolbar);
      }
      if (!this.SSEOutputLoaded) return page;
      if (this.SSEDashboardToolbarSettings.viewMode === "multiple") {
        const SSEMultipleProject = {
          component: OutputSSEMultipleProject,
          props: {
            yearlyResults: this.allYearlyData,
            inputCase: this.currentCase,
            logoUrl: this.logoUrl,
            yearRange: Object.keys(this.SSEYearlyData),
          },
        };
        page.sections.push(SSEMultipleProject);
      }
      if (
        this.SSEToolbarSettings.storages &&
        this.SSEToolbarSettings.storages.length &&
        this.SSEDashboardToolbarSettings.viewMode === "single"
      ) {
        const { viewMode } = this.SSEToolbarSettings;
        let panelChildren;
        if (viewMode === "annual") {
          panelChildren = Object.values(
            this.SSEYearlyData[Object.keys(this.SSEYearlyData)[0]].storages
          ).map((v) => ({
            title: `${v.name}`,
            children: [
              {
                title: `storage-${v.id}`,
                component: OutputSection,
                props: {
                  panel: [0, 1],
                  section: {
                    title: `storage-${v.id}-items`,
                    children: [
                      {
                        title: "Summary",
                        component: Plotly,
                        props: {
                          toImageButtonOptions: this.chartDownloadName(
                            this.caseName,
                            "SSE",
                            "Summary"
                          ),
                          layout: {
                            images: [this.acelerexLogo],
                            yaxis: {
                              title: "$",
                              fixedrange: true,
                            },
                            xaxis: {
                              dtick: 1,
                            },
                            barmode: "stack",
                          },
                          data: (() => {
                            const storageOutput = Object.values(
                              this.SSEYearlyData[this.SSEStartYear].storages
                            ).find((s) => s.id === v.id);
                            const mappedServiceKeys = Object.keys(
                              this.SSEActiveProjectInput.storages.find(
                                (s) => s.id === v.id || s.name === v.name
                              ).services
                            )
                              .filter((service) => service !== "reserves")
                              .map((el) => this.mapServiceToOutputKey(el));
                            const serviceBars = Object.keys(storageOutput)
                              .filter((key) => mappedServiceKeys.includes(key))
                              .map((service) => ({
                                type: "bar",
                                ...this.getAnnualChartData("storages", v.id, "pL", {
                                  serviceKey: service,
                                }),
                                name: `${this.mapServiceKey(service)} P&L [$]`,
                                showlegend: true,
                                marker: { color: this.mapServiceColor(service) },
                              }));
                            const reserveBars = storageOutput.R
                              ? Object.keys(storageOutput.R).map((reserveName, i) => ({
                                  type: "bar",
                                  ...this.getAnnualChartData("storages", v.id, "pL", {
                                    reserve: reserveName,
                                  }),
                                  name: `Res. ${reserveName}`,
                                  marker: { color: this.mapReserveColor(i) },
                                }))
                              : [];
                            const bars = [...serviceBars, ...reserveBars];
                            return bars.length > 0
                              ? bars
                              : [
                                  {
                                    type: "bar",
                                    ...this.getAnnualChartData("storages", v.id, "pL"),
                                    name: "Total P&L [$]",
                                    showlegend: true,
                                    marker: { color: "#0CB5AE" },
                                  },
                                ];
                          })(),
                        },
                      },
                      {
                        title: "Total",
                        component: DataTableYearly,
                        props: {
                          year_start: this.SSEStartYear,
                          items: [
                            {
                              name: "Annual Consumption (MWh)",
                              value: this.getAnnualValue("storages", v.id, "charge"),
                            },
                            {
                              name: "Annual Generation (MWh)",
                              value: this.getAnnualValue("storages", v.id, "discharge"),
                            },
                            {
                              name: "Annual Cycle Count",
                              value: this.getAnnualValue("storages", v.id, "cycles"),
                            },
                            {
                              name: "Annual Energy Throughput (MWh-yr)",
                              value: this.getAnnualValue("storages", v.id, "energyThroughput"),
                            },
                            {
                              name: "Annual Purchases ($-yr)",
                              value: this.getAnnualValue("storages", v.id, "purchases"),
                            },
                            {
                              name: "Annual Sales  ($-yr)",
                              value: this.getAnnualValue("storages", v.id, "sales"),
                            },
                            {
                              name: "Annual P&L ($)",
                              value: this.getAnnualValue("storages", v.id, "pL"),
                            },
                            {
                              name: "Annual Operation Hours (h)",
                              value: this.getAnnualValue("storages", v.id, "operationHours"),
                            },
                          ],
                        },
                      },
                      ...Object.keys(
                        Object.values(this.SSEYearlyData[this.SSEStartYear].storages).find(
                          (s) => s.id === v.id
                        )
                      )
                        .filter((key) =>
                          Object.keys(
                            this.SSEActiveProjectInput.storages.find(
                              (s) => s.id === v.id || s.name === v.name
                            ).services
                          )
                            .filter((service) => service !== "reserves")
                            .map((serviceKey) => this.mapServiceToOutputKey(serviceKey))
                            .includes(key)
                        )
                        .map((service) => ({
                          title: this.mapServiceKey(service),
                          component: DataTableYearly,
                          props: {
                            year_start: this.SSEStartYear,
                            items: [
                              {
                                name: "Purchases ($-yr)",
                                value: this.getAnnualValue("storages", v.id, service, "purchases"),
                              },
                              {
                                name: "Sales ($-yr)",
                                value: this.getAnnualValue("storages", v.id, service, "sales"),
                              },
                              {
                                name: "Power Allocation (MW-yr)",
                                value: this.getAnnualValue(
                                  "storages",
                                  v.id,
                                  service,
                                  "powerAllocation"
                                ),
                              },
                              {
                                name: "Energy Allocation (MWh-yr)",
                                value: this.getAnnualValue(
                                  "storages",
                                  v.id,
                                  service,
                                  "energyAllocation"
                                ),
                              },
                              {
                                name: "Consumption (MWh-yr)",
                                value: this.getAnnualValue("storages", v.id, service, "charge"),
                              },
                              {
                                name: "Generation (MWh-yr)",
                                value: this.getAnnualValue("storages", v.id, service, "discharge"),
                              },
                              {
                                name: "P&L ($)",
                                value: this.getAnnualValue("storages", v.id, service, "pL"),
                              },
                              {
                                name: "Operation Hours (h) ($)",
                                value: this.getAnnualValue(
                                  "storages",
                                  v.id,
                                  service,
                                  "operationHours"
                                ),
                              },
                            ],
                          },
                        })),
                      ...(Object.values(this.SSEYearlyData[this.SSEStartYear].storages).find(
                        (s) => s.id === v.id
                      ).R
                        ? Object.keys(
                            Object.values(this.SSEYearlyData[this.SSEStartYear].storages).find(
                              (s) => s.id === v.id
                            ).R
                          ).map((reserveName) => ({
                            title: `Reserve: ${reserveName}`,
                            component: DataTableYearly,
                            props: {
                              year_start: this.SSEStartYear,
                              items: [
                                {
                                  name: "Purchases ($-yr)",
                                  value: this.getReserveAnnualValue(
                                    "storages",
                                    v.id,
                                    "purchases",
                                    reserveName
                                  ),
                                },
                                {
                                  name: "Sales ($-yr)",
                                  value: this.getReserveAnnualValue(
                                    "storages",
                                    v.id,
                                    "sales",
                                    reserveName
                                  ),
                                },
                                {
                                  name: "Power Allocation (MW-yr)",
                                  value: this.getReserveAnnualValue(
                                    "storages",
                                    v.id,
                                    "powerAllocation",
                                    reserveName
                                  ),
                                },
                                {
                                  name: "Energy Allocation (MWh-yr)",
                                  value: this.getReserveAnnualValue(
                                    "storages",
                                    v.id,
                                    "energyAllocation",
                                    reserveName
                                  ),
                                },
                                {
                                  name: "Consumption (MWh-yr)",
                                  value: this.getReserveAnnualValue(
                                    "storages",
                                    v.id,
                                    "discharge",
                                    reserveName
                                  ),
                                },
                                {
                                  name: "Generation (MWh-yr)",
                                  value: this.getReserveAnnualValue(
                                    "storages",
                                    v.id,
                                    "charge",
                                    reserveName
                                  ),
                                },
                                {
                                  name: "P&L ($)",
                                  value: this.getReserveAnnualValue(
                                    "storages",
                                    v.id,
                                    "pL",
                                    reserveName
                                  ),
                                },
                                {
                                  name: "Operation Hours (h)",
                                  value: this.getReserveAnnualValue(
                                    "storages",
                                    v.id,
                                    "operationHours",
                                    reserveName
                                  ),
                                },
                              ],
                            },
                          }))
                        : []),
                    ],
                  },
                },
              },
            ],
          }));
        } else if (viewMode === "monthly") {
          panelChildren = this.SSEMonthlyData.storages
            ? Object.entries(this.SSEMonthlyData.storages || {}).map(([key, v], i) => ({
                title: key,
                children: [
                  {
                    title: `storage-${i}`,
                    component: OutputSection,
                    props: {
                      section: {
                        title: `storage-${i}-items`,
                        children: [
                          {
                            title: "Monthly P&L",
                            component: Plotly,
                            props: {
                              toImageButtonOptions: this.chartDownloadName(
                                this.caseName,
                                "SSE",
                                "Monthly P&L"
                              ),
                              layout: {
                                images: [this.acelerexLogo],
                                yaxis: {
                                  title: "$",
                                },
                                xaxis: {
                                  tickformat:
                                    this.SSEToolbarSettings.selectedYear.length >= 2 ? "%m" : "",
                                },
                                barmode: "relative",
                              },
                              data: this.SSEToolbarSettings.selectedYear.length
                                ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                    name: year,
                                    ...this.getChartData(v, "pL", {
                                      dataType: "monthly",
                                      selectedYear: year,
                                    }),
                                    line: {
                                      shape: "hv",
                                      color: "#000000",
                                    },
                                    type: "bar",
                                  }))
                                : [
                                    {
                                      name: "P&L",
                                      ...this.getChartData(v, "pL", {
                                        dataType: "monthly",
                                        selectedYear: Object.keys(v)[0],
                                      }),
                                      line: {
                                        shape: "hv",
                                        color: "#000000",
                                      },
                                      type: "bar",
                                    },
                                  ],
                            },
                          },
                          {
                            title: "Monthly P&L Duration Curve",
                            component: Plotly,
                            props: {
                              toImageButtonOptions: this.chartDownloadName(
                                this.caseName,
                                "SSE",
                                "Monthly P&L Duration Curve"
                              ),
                              layout: {
                                images: [this.acelerexLogo],
                                yaxis: {
                                  title: "$",
                                  fixedrange: true,
                                },
                                xaxis: {
                                  type:
                                    this.SSEToolbarSettings.selectedYear.length >= 2
                                      ? ""
                                      : "category",
                                },
                                barmode: "relative",
                              },
                              data: this.SSEToolbarSettings.selectedYear.length
                                ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                    name: year,
                                    ...this.getChartData(v, "pL", {
                                      dataType: "monthly",
                                      selectedYear: year,
                                      sort: true,
                                    }),
                                    line: {
                                      shape: "hv",
                                      color: "#000000",
                                    },
                                    type: "bar",
                                  }))
                                : [
                                    {
                                      name: "P&L",
                                      ...this.getChartData(v, "pL", {
                                        dataType: "monthly",
                                        selectedYear: Object.keys(v)[0],
                                        sort: true,
                                      }),
                                      line: {
                                        shape: "hv",
                                        color: "#000000",
                                      },
                                      type: "bar",
                                    },
                                  ],
                            },
                          },
                          {
                            title: "Monthly P&L/Cycle",
                            component: Plotly,
                            props: {
                              toImageButtonOptions: this.chartDownloadName(
                                this.caseName,
                                "SSE",
                                "Monthly P&L/Cycle"
                              ),
                              layout: {
                                images: [this.acelerexLogo],
                                yaxis: {
                                  title: "",
                                  fixedrange: true,
                                },
                                xaxis: {
                                  tickformat:
                                    this.SSEToolbarSettings.selectedYear.length >= 2 ? "%d/%m" : "",
                                },

                                barmode: "relative",
                              },
                              data: this.SSEToolbarSettings.selectedYear.length
                                ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                    name: year,
                                    ...this.getChartData(v, "pLPerCycles", {
                                      dataType: "daily",
                                      selectedYear: year,
                                    }),
                                    line: {
                                      shape: "hv",
                                      color: "#000000",
                                    },
                                    type: "bar",
                                  }))
                                : [
                                    {
                                      name: "P&L/Cycle",
                                      ...this.getChartData(v, "pLPerCycles", {
                                        dataType: "daily",
                                        selectedYear: Object.keys(v)[0],
                                      }),
                                      line: {
                                        shape: "hv",
                                        color: "#000000",
                                      },
                                      type: "bar",
                                    },
                                  ],
                            },
                          },
                          {
                            title: "Monthly P&L/Cycle Duration Curve",
                            component: Plotly,
                            props: {
                              toImageButtonOptions: this.chartDownloadName(
                                this.caseName,
                                "SSE",
                                "Monthly P&L/Cycle Duration Curve"
                              ),
                              layout: {
                                images: [this.acelerexLogo],
                                yaxis: {
                                  title: "$",
                                  fixedrange: true,
                                },
                                xaxis: {
                                  type:
                                    this.SSEToolbarSettings.selectedYear.length >= 2
                                      ? ""
                                      : "category",
                                },
                                barmode: "relative",
                              },
                              data: this.SSEToolbarSettings.selectedYear.length
                                ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                    name: year,
                                    ...this.getChartData(v, "pLPerCycles", {
                                      dataType: "monthly",
                                      selectedYear: year,
                                      sort: true,
                                    }),
                                    line: {
                                      shape: "hv",
                                      color: "#000000",
                                    },
                                    type: "bar",
                                  }))
                                : [
                                    {
                                      name: "P&L/Cycle",
                                      ...this.getChartData(v, "pLPerCycles", {
                                        dataType: "monthly",
                                        selectedYear: Object.keys(v)[0],
                                        sort: true,
                                      }),
                                      line: {
                                        shape: "hv",
                                        color: "#000000",
                                      },
                                      type: "bar",
                                    },
                                  ],
                            },
                          },
                          {
                            title: "Monthly Energy Purchases",
                            component: Plotly,
                            props: {
                              toImageButtonOptions: this.chartDownloadName(
                                this.caseName,
                                "SSE",
                                "Monthly Energy Purchases"
                              ),
                              layout: {
                                images: [this.acelerexLogo],
                                yaxis: {
                                  title: "$-m",
                                  fixedrange: true,
                                },
                                xaxis: {
                                  tickformat:
                                    this.SSEToolbarSettings.selectedYear.length >= 2 ? "%d/%m" : "",
                                },

                                barmode: "relative",
                              },
                              data: this.SSEToolbarSettings.selectedYear.length
                                ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                    name: year,
                                    ...this.getChartData(v, "totalEnergyPurchases", {
                                      dataType: "monthly",
                                      selectedYear: year,
                                    }),
                                    line: {
                                      shape: "hv",
                                      color: "#000000",
                                    },
                                    type: "bar",
                                  }))
                                : [
                                    {
                                      name: "Energy Purchases [$-d]",
                                      ...this.getChartData(v, "totalEnergyPurchases", {
                                        dataType: "monthly",
                                        selectedYear: Object.keys(v)[0],
                                      }),
                                      line: {
                                        shape: "hv",
                                        color: "#000000",
                                      },
                                      type: "bar",
                                    },
                                  ],
                            },
                          },
                          {
                            title: "Monthly Energy Sales",
                            component: Plotly,
                            props: {
                              toImageButtonOptions: this.chartDownloadName(
                                this.caseName,
                                "SSE",
                                "Monthly Energy Sales"
                              ),
                              layout: {
                                images: [this.acelerexLogo],
                                yaxis: {
                                  title: "$-m",
                                  fixedrange: true,
                                },
                                xaxis: {
                                  tickformat:
                                    this.SSEToolbarSettings.selectedYear.length >= 2 ? "%d/%m" : "",
                                },

                                barmode: "relative",
                              },
                              data: this.SSEToolbarSettings.selectedYear.length
                                ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                    name: year,
                                    ...this.getChartData(v, "totalEnergySales", {
                                      dataType: "monthly",
                                      selectedYear: year,
                                    }),
                                    line: {
                                      shape: "hv",
                                      color: "#000000",
                                    },
                                    type: "bar",
                                  }))
                                : [
                                    {
                                      name: "Energy Sales [$-d]",
                                      ...this.getChartData(v, "totalEnergySales", {
                                        dataType: "monthly",
                                        selectedYear: Object.keys(v)[0],
                                      }),
                                      line: {
                                        shape: "hv",
                                        color: "#000000",
                                      },
                                      type: "bar",
                                    },
                                  ],
                            },
                          },
                          {
                            title: "Monthly Energy Throughput",
                            component: Plotly,
                            props: {
                              toImageButtonOptions: this.chartDownloadName(
                                this.caseName,
                                "SSE",
                                "Monthly Energy Throughput"
                              ),
                              layout: {
                                images: [this.acelerexLogo],
                                yaxis: {
                                  title: "MWh-m",
                                  fixedrange: true,
                                },
                                xaxis: {
                                  tickformat:
                                    this.SSEToolbarSettings.selectedYear.length >= 2 ? "%d/%m" : "",
                                },

                                barmode: "relative",
                              },
                              data: this.SSEToolbarSettings.selectedYear.length
                                ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                    name: year,
                                    ...this.getChartData(v, "energyThroughput", {
                                      dataType: "monthly",
                                      selectedYear: year,
                                    }),
                                    line: {
                                      shape: "hv",
                                      color: "#000000",
                                    },
                                    type: "bar",
                                  }))
                                : [
                                    {
                                      name: "Energy Throughput [MWh-d]",
                                      ...this.getChartData(v, "energyThroughput", {
                                        dataType: "monthly",
                                        selectedYear: Object.keys(v)[0],
                                      }),
                                      line: {
                                        shape: "hv",
                                        color: "#000000",
                                      },
                                      type: "bar",
                                    },
                                  ],
                            },
                          },
                          {
                            title: "Monthly Cycles",
                            component: Plotly,
                            props: {
                              toImageButtonOptions: this.chartDownloadName(
                                this.caseName,
                                "SSE",
                                "Monthly Cycles"
                              ),
                              layout: {
                                images: [this.acelerexLogo],
                                yaxis: {
                                  title: "",
                                  fixedrange: true,
                                },
                                xaxis: {
                                  tickformat:
                                    this.SSEToolbarSettings.selectedYear.length >= 2 ? "%d/%m" : "",
                                },

                                barmode: "relative",
                              },
                              data: this.SSEToolbarSettings.selectedYear.length
                                ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                    name: year,
                                    ...this.getChartData(v, "cycles", {
                                      dataType: "monthly",
                                      selectedYear: year,
                                    }),
                                    line: {
                                      shape: "hv",
                                      color: "#000000",
                                    },
                                    type: "bar",
                                  }))
                                : [
                                    {
                                      name: "Cycles",
                                      ...this.getChartData(v, "cycles", {
                                        dataType: "monthly",
                                        selectedYear: Object.keys(v)[0],
                                      }),
                                      line: {
                                        shape: "hv",
                                        color: "#000000",
                                      },
                                      type: "bar",
                                    },
                                  ],
                            },
                          },
                          ...Object.entries(this.SSEActiveProjectInput.storages[i].services)
                            .filter((entry) => entry[1].active)
                            .map(([serviceKey, value]) => [
                              this.mapServiceToOutputKey(serviceKey),
                              value,
                            ])
                            .filter((service) => service[0] !== undefined)
                            .map((service) => ({
                              title: this.mapServiceKey(service[0]),
                              component: OutputSection,
                              props: {
                                section: {
                                  title: "items",
                                  children: [
                                    {
                                      title: "Purchases & Sales ($)",
                                      component: Plotly,
                                      props: {
                                        toImageButtonOptions: this.chartDownloadName(
                                          this.caseName,
                                          "SSE",
                                          `${this.mapServiceKey(
                                            service[0]
                                          )} Monthly Purchases & Sales ($)`
                                        ),
                                        layout: {
                                          images: [this.acelerexLogo],
                                          barmode: "relative",
                                          xaxis: {
                                            tickformat:
                                              this.SSEToolbarSettings.selectedYear.length >= 2
                                                ? "%d/%m"
                                                : "",
                                          },
                                        },
                                        data: this.SSEToolbarSettings.selectedYear.length
                                          ? this.SSEToolbarSettings.selectedYear.flatMap((year) => [
                                              {
                                                name: `${year} Purchases [$]`,
                                                ...this.getChartData(v, "purchases", {
                                                  valueModifier: (val) => val * -1,
                                                  serviceKey: service[0],
                                                  dataType: "monthly",
                                                  selectedYear: year,
                                                }),
                                                type: "bar",
                                              },
                                              {
                                                name: `${year} Sales [$]`,
                                                ...this.getChartData(v, "sales", {
                                                  serviceKey: service[0],
                                                  dataType: "monthly",
                                                  selectedYear: year,
                                                }),
                                                type: "bar",
                                              },
                                            ])
                                          : [
                                              {
                                                name: "Purchases [$]",
                                                ...this.getChartData(v, "purchases", {
                                                  valueModifier: (val) => val * -1,
                                                  serviceKey: service[0],
                                                  dataType: "monthly",
                                                  selectedYear: Object.keys(v)[0],
                                                }),
                                                type: "bar",
                                              },
                                              {
                                                name: "Sales [$]",
                                                ...this.getChartData(v, "sales", {
                                                  serviceKey: service[0],
                                                  dataType: "monthly",
                                                  selectedYear: Object.keys(v)[0],
                                                }),
                                                type: "bar",
                                              },
                                            ],
                                      },
                                    },
                                    {
                                      title: "Power Allocation (MW)",
                                      component: Plotly,
                                      props: {
                                        toImageButtonOptions: this.chartDownloadName(
                                          this.caseName,
                                          "SSE",
                                          `${this.mapServiceKey(
                                            service[0]
                                          )} Monthly Power Allocation (MW)`
                                        ),
                                        layout: {
                                          images: [this.acelerexLogo],
                                          yaxis: {
                                            title: "Power [MW]",
                                            range: [
                                              0,
                                              Math.max(
                                                ...this.SSEActiveProjectInput.storages[i]
                                                  .power_capacity
                                              ) + 0.5,
                                            ],
                                            fixedrange: true,
                                          },
                                          xaxis: {
                                            tickformat:
                                              this.SSEToolbarSettings.selectedYear.length >= 2
                                                ? "%d/%m"
                                                : "",
                                          },
                                        },
                                        data: this.SSEToolbarSettings.selectedYear.length
                                          ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                              name: `${this.mapServiceKey(service[0])} ${year}`,
                                              ...this.getChartData(v, "powerAllocation", {
                                                serviceKey: service[0],
                                                dataType: "monthly",
                                                selectedYear: year,
                                              }),
                                              type: "bar",
                                            }))
                                          : [
                                              {
                                                name: this.mapServiceKey(service[0]),
                                                ...this.getChartData(v, "powerAllocation", {
                                                  serviceKey: service[0],
                                                  dataType: "monthly",
                                                  selectedYear: Object.keys(v)[0],
                                                }),
                                                type: "bar",
                                              },
                                            ],
                                      },
                                    },
                                    {
                                      title: "Energy Allocation (MWh)",
                                      component: Plotly,
                                      props: {
                                        toImageButtonOptions: this.chartDownloadName(
                                          this.caseName,
                                          "SSE",
                                          `${this.mapServiceKey(
                                            service[0]
                                          )} Monthly Energy Allocation (MWh)`
                                        ),
                                        layout: {
                                          images: [this.acelerexLogo],
                                          yaxis: {
                                            title: "Energy [MWh]",
                                            range: [
                                              0,
                                              Math.max(
                                                ...this.SSEActiveProjectInput.storages[i]
                                                  .energy_capacity
                                              ) + 0.5,
                                            ],
                                            fixedrange: true,
                                            xaxis: {
                                              tickformat:
                                                this.SSEToolbarSettings.selectedYear.length >= 2
                                                  ? "%d/%m"
                                                  : "",
                                            },
                                          },
                                        },
                                        data: this.SSEToolbarSettings.selectedYear.length
                                          ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                              name: `${this.mapServiceKey(service[0])} ${year}`,
                                              ...this.getChartData(v, "energyAllocation", {
                                                serviceKey: service[0],
                                                dataType: "monthly",
                                                selectedYear: year,
                                              }),
                                              type: "bar",
                                            }))
                                          : [
                                              {
                                                name: this.mapServiceKey(service[0]),
                                                ...this.getChartData(v, "energyAllocation", {
                                                  serviceKey: service[0],
                                                  dataType: "monthly",
                                                  selectedYear: Object.keys(v)[0],
                                                }),
                                                type: "bar",
                                              },
                                            ],
                                      },
                                    },
                                  ],
                                },
                              },
                            })),
                          ...(Object.values(v)[0][0].R
                            ? Object.keys(Object.values(v)[0][0].R).map((reserveName) => ({
                                title: `Reserve: ${reserveName}`,
                                component: OutputSection,
                                props: {
                                  section: {
                                    title: "items",
                                    children: [
                                      {
                                        title: "Purchases & Sales ($)",
                                        component: Plotly,
                                        props: {
                                          toImageButtonOptions: this.chartDownloadName(
                                            this.caseName,
                                            "SSE",
                                            `${reserveName} Monthly Purchases & Sales ($)`
                                          ),
                                          layout: {
                                            images: [this.acelerexLogo],
                                            barmode: "relative",
                                            xaxis: {
                                              tickformat:
                                                this.SSEToolbarSettings.selectedYear.length >= 2
                                                  ? "%d/%m"
                                                  : "",
                                            },
                                          },
                                          data: this.SSEToolbarSettings.selectedYear.length
                                            ? this.SSEToolbarSettings.selectedYear.flatMap(
                                                (year) => [
                                                  {
                                                    name: `${year} Purchases [$] `,
                                                    ...this.getReserveChartData(
                                                      v,
                                                      reserveName,
                                                      "purchases",
                                                      {
                                                        valueModifier: (val) => val * -1,
                                                        dataType: "monthly",
                                                        selectedYear: year,
                                                      }
                                                    ),
                                                    type: "bar",
                                                  },
                                                  {
                                                    name: `${year} Sales [$]`,
                                                    ...this.getReserveChartData(
                                                      v,
                                                      reserveName,
                                                      "sales",
                                                      {
                                                        dataType: "monthly",
                                                        selectedYear: year,
                                                      }
                                                    ),
                                                    type: "bar",
                                                  },
                                                ]
                                              )
                                            : [
                                                {
                                                  name: "Purchases [$]",
                                                  ...this.getReserveChartData(
                                                    v,
                                                    reserveName,
                                                    "purchases",
                                                    {
                                                      valueModifier: (val) => val * -1,
                                                      dataType: "monthly",
                                                      selectedYear: Object.keys(v)[0],
                                                    }
                                                  ),
                                                  type: "bar",
                                                },
                                                {
                                                  name: "Sales [$]",
                                                  ...this.getReserveChartData(
                                                    v,
                                                    reserveName,
                                                    "sales",
                                                    {
                                                      dataType: "monthly",
                                                      selectedYear: Object.keys(v)[0],
                                                    }
                                                  ),
                                                  type: "bar",
                                                },
                                              ],
                                        },
                                      },
                                      {
                                        title: "Power Allocation (MW)",
                                        component: Plotly,
                                        props: {
                                          toImageButtonOptions: this.chartDownloadName(
                                            this.caseName,
                                            "SSE",
                                            `${reserveName} Monthly Power Allocation (MW)`
                                          ),
                                          layout: {
                                            images: [this.acelerexLogo],
                                            yaxis: {
                                              title: "Power [MW]",
                                              range: [
                                                0,
                                                Math.max(
                                                  ...this.SSEActiveProjectInput.storages[i]
                                                    .power_capacity
                                                ) + 0.5,
                                              ],
                                              fixedrange: true,
                                              xaxis: {
                                                tickformat:
                                                  this.SSEToolbarSettings.selectedYear.length >= 2
                                                    ? "%d/%m"
                                                    : "",
                                              },
                                            },
                                          },
                                          data: this.SSEToolbarSettings.selectedYear.length
                                            ? this.SSEToolbarSettings.selectedYear.map(() => ({
                                                ...this.getReserveChartData(
                                                  v,
                                                  reserveName,
                                                  "powerAllocation",
                                                  {
                                                    dataType: "monthly",
                                                    selectedYear: Object.keys(v)[0],
                                                  }
                                                ),
                                                type: "bar",
                                              }))
                                            : [
                                                {
                                                  ...this.getReserveChartData(
                                                    v,
                                                    reserveName,
                                                    "powerAllocation",
                                                    {
                                                      dataType: "monthly",
                                                      selectedYear: Object.keys(v)[0],
                                                    }
                                                  ),
                                                  type: "bar",
                                                },
                                              ],
                                        },
                                      },
                                      {
                                        title: "Energy Allocation (MWh)",
                                        component: Plotly,
                                        props: {
                                          toImageButtonOptions: this.chartDownloadName(
                                            this.caseName,
                                            "SSE",
                                            `${reserveName} Monthly Energy Allocation (MWh)`
                                          ),
                                          layout: {
                                            images: [this.acelerexLogo],
                                            yaxis: {
                                              title: "Energy [MWh]",
                                              range: [
                                                0,
                                                Math.max(
                                                  ...this.SSEActiveProjectInput.storages[i]
                                                    .energy_capacity
                                                ) + 0.5,
                                              ],
                                              fixedrange: true,
                                              xaxis: {
                                                tickformat:
                                                  this.SSEToolbarSettings.selectedYear.length >= 2
                                                    ? "%d/%m"
                                                    : "",
                                              },
                                            },
                                          },
                                          data: this.SSEToolbarSettings.selectedYear.length
                                            ? this.SSEToolbarSettings.selectedYear.map(() => ({
                                                ...this.getReserveChartData(
                                                  v,
                                                  reserveName,
                                                  "energyAllocation",
                                                  {
                                                    dataType: "monthly",
                                                    selectedYear: Object.keys(v)[0],
                                                  }
                                                ),
                                                type: "bar",
                                              }))
                                            : [
                                                {
                                                  ...this.getReserveChartData(
                                                    v,
                                                    reserveName,
                                                    "energyAllocation",
                                                    {
                                                      dataType: "monthly",
                                                      selectedYear: Object.keys(v)[0],
                                                    }
                                                  ),
                                                  type: "bar",
                                                },
                                              ],
                                        },
                                      },
                                    ],
                                  },
                                },
                              }))
                            : []),
                        ],
                      },
                    },
                  },
                ],
              }))
            : [];
        } else if (viewMode === "timeSeries") {
          panelChildren = this.SSETimeseriesData.storages
            ? Object.entries(this.SSETimeseriesData.storages).map(([key, v], i) => ({
                title: key,
                children: [
                  {
                    title: `storage-${i}`,
                    component: OutputSection,
                    props: {
                      panel: [0],
                      section: {
                        title: `storage-${i}-items`,
                        children: [
                          {
                            title: "Summary",
                            component: OutputSection,
                            props: {
                              panel: [0, 3],
                              section: {
                                title: "items",
                                children: [
                                  {
                                    title: "Active Power (P)",
                                    component: ChartTimeseries,
                                    props: {
                                      toImageButtonOptions: this.chartDownloadName(
                                        this.caseName,
                                        "SSE",
                                        "Active Power (P)"
                                      ),
                                      layout: {
                                        barmode: "relative",
                                        yaxis: {
                                          title: "Power [MW]",
                                          range: [
                                            -(
                                              Math.max(
                                                ...this.SSEActiveProjectInput.storages[i]
                                                  .power_capacity
                                              ) + 1
                                            ),
                                            Math.max(
                                              ...this.SSEActiveProjectInput.storages[i]
                                                .power_capacity
                                            ) + 1,
                                          ],
                                          fixedrange: true,
                                        },
                                        annotations: [
                                          {
                                            xref: "paper",
                                            yref: "paper",
                                            x: 0.01,
                                            y: 0.6,
                                            text: "Discharge",
                                            yanchor: "bottom",
                                            showarrow: false,
                                            bgcolor: "#ffffff",
                                            textangle: -90,
                                          },
                                          {
                                            xref: "paper",
                                            yref: "paper",
                                            x: 0.01,
                                            y: 0.4,
                                            text: "Charge",
                                            yanchor: "top",
                                            showarrow: false,
                                            bgcolor: "#ffffff",
                                            textangle: -90,
                                          },
                                        ],
                                      },
                                      data: [
                                        {
                                          name: "Total Power Nomination",
                                          ...this.getChartData(v, "totPowNom", {
                                            duplicateLast: true,
                                          }),
                                          line: {
                                            shape: "hv",
                                            color: "#000000",
                                          },
                                        },
                                        ...Object.keys(v[0])
                                          .filter((serviceKey) =>
                                            this.availableServices.includes(serviceKey)
                                          )
                                          .map((service) => ({
                                            name: this.mapServiceKey(service),
                                            ...this.powerNominationData(v, service),
                                            type: "bar",
                                            marker: {
                                              color: this.mapServiceColor(service),
                                            },
                                            offset: 0.5,
                                            width:
                                              this.currentCase.settings.timeline_settings
                                                .time_interval *
                                              60 *
                                              1000,
                                          })),
                                        ...(v[0].R
                                          ? Object.keys(v[0].R).map((reserveKey, reserveIndex) => ({
                                              name: `Res. ${reserveKey}`,
                                              ...this.reservePowerNominationData(v, reserveKey),
                                              type: "bar",
                                              marker: {
                                                color: this.mapReserveColor(reserveIndex),
                                              },
                                              offset: 0.5,
                                              width:
                                                this.currentCase.settings.timeline_settings
                                                  .time_interval *
                                                60 *
                                                1000,
                                            }))
                                          : []),
                                      ],
                                    },
                                  },
                                  {
                                    title: "Reactive Power (Q)",
                                    component: ChartTimeseries,
                                    props: {
                                      toImageButtonOptions: this.chartDownloadName(
                                        this.caseName,
                                        "SSE",
                                        "Reactive Power (Q)"
                                      ),
                                      layout: {
                                        yaxis: {
                                          title: "Power [MVAR]",
                                          range: [
                                            -(
                                              Math.max(
                                                ...this.SSEActiveProjectInput.storages[i]
                                                  .power_capacity
                                              ) + 1
                                            ),
                                            Math.max(
                                              ...this.SSEActiveProjectInput.storages[i]
                                                .power_capacity
                                            ) + 1,
                                          ],
                                          fixedrange: true,
                                        },
                                        barmode: "stack",
                                        annotations: [
                                          {
                                            xref: "paper",
                                            yref: "paper",
                                            x: 0.01,
                                            y: 0.6,
                                            text: "Discharge",
                                            yanchor: "bottom",
                                            showarrow: false,
                                            bgcolor: "#ffffff",
                                            textangle: -90,
                                          },
                                          {
                                            xref: "paper",
                                            yref: "paper",
                                            x: 0.01,
                                            y: 0.4,
                                            text: "Charge",
                                            yanchor: "top",
                                            showarrow: false,
                                            bgcolor: "#ffffff",
                                            textangle: -90,
                                          },
                                        ],
                                      },
                                      data: [
                                        {
                                          name: "Total Reactive Power",
                                          ...this.getChartData(v, "totalReactivePower", {
                                            duplicateLast: true,
                                          }),
                                          line: {
                                            shape: "hv",
                                            color: "#000000",
                                          },
                                        },
                                        ...Object.keys(v[0])
                                          .filter((serviceKey) => ["FC"].includes(serviceKey))
                                          .map((service) => ({
                                            name: this.mapServiceKey(service),
                                            ...this.powerNominationData(v, service),
                                            type: "bar",
                                            marker: {
                                              color: this.mapServiceColor(service),
                                            },
                                            offset: 0.5,
                                            width:
                                              this.currentCase.settings.timeline_settings
                                                .time_interval *
                                              60 *
                                              1000,
                                          })),
                                      ],
                                    },
                                  },
                                  {
                                    title: "Apparent Power (S)",
                                    component: ApparentPowerTimeseries,
                                    props: {
                                      layout: {
                                        yaxis: {
                                          title: "Power",
                                          fixedrange: true,
                                          range: [
                                            -(
                                              Math.max(
                                                ...this.SSEActiveProjectInput.storages[i]
                                                  .power_capacity
                                              ) + 1
                                            ),
                                            Math.max(
                                              ...this.SSEActiveProjectInput.storages[i]
                                                .power_capacity
                                            ) + 1,
                                          ],
                                        },
                                      },
                                      activePower: this.getChartData(v, "totPowNom", {
                                        duplicateLast: true,
                                      }),
                                      reactivePower: this.getChartData(v, "totalReactivePower", {
                                        duplicateLast: true,
                                      }),
                                    },
                                  },
                                  {
                                    title: "Power Allocation (MW)",
                                    component: ChartTimeseries,
                                    props: {
                                      toImageButtonOptions: this.chartDownloadName(
                                        this.caseName,
                                        "SSE",
                                        "Power Allocation (MW)"
                                      ),
                                      layout: {
                                        yaxis: {
                                          title: "Power [MW]",
                                          range: [
                                            0,
                                            Math.max(
                                              ...this.SSEActiveProjectInput.storages[i]
                                                .power_capacity
                                            ) + 0.5,
                                          ],
                                          fixedrange: true,
                                        },
                                        barmode: "stack",
                                      },
                                      data: [
                                        ...Object.keys(v[0])
                                          .filter((entryKey) =>
                                            this.availableServices.includes(entryKey)
                                          )
                                          .map((serviceKey) => ({
                                            name: this.mapServiceKey(serviceKey),
                                            type: "bar",
                                            ...this.getChartData(v, "powAlloc", { serviceKey }),
                                          })),
                                        ...(v[0].R
                                          ? Object.keys(v[0].R).map((reserveKey, reserveIndex) => ({
                                              name: `Res. ${reserveKey}`,
                                              ...this.getReserveChartData(
                                                v,
                                                reserveKey,
                                                "powAlloc"
                                              ),
                                              type: "bar",
                                              marker: {
                                                color: this.mapReserveColor(reserveIndex),
                                              },
                                            }))
                                          : []),
                                      ],
                                    },
                                  },
                                  {
                                    title: "State of Charge",
                                    component: ChartTimeseries,
                                    props: {
                                      toImageButtonOptions: this.chartDownloadName(
                                        this.caseName,
                                        "SSE",
                                        "State of Charge"
                                      ),
                                      layout: {
                                        yaxis: {
                                          title: "State of Charge [%]",
                                          range: [0, 100],
                                          fixedrange: true,
                                        },
                                        yaxis2: {
                                          title: "State of Charge [MWh]",
                                          range: [
                                            0,
                                            Math.max(
                                              ...this.SSEActiveProjectInput.storages[i]
                                                .energy_capacity
                                            ),
                                          ],
                                          fixedrange: true,
                                          overlaying: "y",
                                          side: "right",
                                        },
                                        barmode: "stack",
                                      },
                                      data: [
                                        {
                                          name: "SoC %",
                                          ...this.getChartData(v, "totSocPu", {
                                            valueModifier: (value) => value * 100,
                                          }),
                                          legendgroup: "group",
                                        },
                                        {
                                          name: "SoC MWh",
                                          ...this.getChartData(v, "totSocMWh"),
                                          yaxis: "y2",
                                          legendgroup: "group",
                                        },
                                        ...Object.entries(
                                          this.SSEActiveProjectInput.storages[i].services
                                        )
                                          .filter((entry) => entry[1].active)
                                          .map((entry) => this.mapServiceToOutputKey(entry[0]))
                                          .filter((serviceKey) => serviceKey !== undefined)
                                          .map((serviceKey) => ({
                                            name: this.mapServiceKey(serviceKey),
                                            ...this.getChartData(v, "SocMWh", { serviceKey }),
                                            type: "bar",
                                            marker: {
                                              color: this.mapServiceColor(serviceKey),
                                            },
                                            yaxis: "y2",
                                            legendgroup: "group2",
                                          })),
                                        ...(v[0].R
                                          ? Object.keys(v[0].R).map((reserveKey, reserveIndex) => ({
                                              name: `Res. ${reserveKey}`,
                                              ...this.getReserveChartData(v, reserveKey, "SocMWh"),
                                              type: "bar",
                                              marker: {
                                                color: this.mapReserveColor(reserveIndex),
                                              },
                                              yaxis: "y2",
                                              legendgroup: "group2",
                                            }))
                                          : []),
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          },
                          ...Object.entries(this.SSEActiveProjectInput.storages[i].services)
                            .filter((entry) => entry[1].active)
                            .map(([serviceKey, value]) => [
                              this.mapServiceToOutputKey(serviceKey),
                              value,
                            ])
                            .filter((service) => service[0] !== undefined)
                            .map((service) => ({
                              title: this.mapServiceKey(service[0]),
                              component: OutputSection,
                              props: {
                                section: {
                                  title: "items",
                                  children: [
                                    {
                                      title: "Power Allocation (MW)",
                                      component: ChartTimeseries,
                                      props: {
                                        toImageButtonOptions: this.chartDownloadName(
                                          this.caseName,
                                          "SSE",
                                          "Power Allocation (MW)"
                                        ),
                                        layout: {
                                          yaxis: {
                                            title: "Power [MW]",
                                            range: [
                                              0,
                                              Math.max(
                                                ...this.SSEActiveProjectInput.storages[i]
                                                  .power_capacity
                                              ) + 0.5,
                                            ],
                                            fixedrange: true,
                                          },
                                        },
                                        data: [
                                          {
                                            name: this.mapServiceKey(service[0]),
                                            ...this.getChartData(v, "powAlloc", {
                                              serviceKey: service[0],
                                            }),
                                            type: "bar",
                                          },
                                        ],
                                      },
                                    },
                                    {
                                      title: "Power Nomination (MW)",
                                      component: ChartTimeseries,
                                      props: {
                                        toImageButtonOptions: this.chartDownloadName(
                                          this.caseName,
                                          "SSE",
                                          "Power Nomination (MW)"
                                        ),
                                        layout: {
                                          yaxis: {
                                            title: "Power [MW]",
                                            range: [
                                              -(
                                                Math.max(
                                                  ...this.SSEActiveProjectInput.storages[i]
                                                    .power_capacity
                                                ) + 0.5
                                              ),
                                              Math.max(
                                                ...this.SSEActiveProjectInput.storages[i]
                                                  .power_capacity
                                              ) + 0.5,
                                            ],
                                            fixedrange: true,
                                          },
                                          ...(service[0] === "EA"
                                            ? {
                                                yaxis2: {
                                                  title: "Energy Price [$/MWh]",
                                                  overlaying: "y",
                                                  side: "right",
                                                },
                                              }
                                            : {}),
                                        },
                                        data: [
                                          {
                                            name: this.mapServiceKey(service[0]),
                                            ...this.getChartData(v, "powNom", {
                                              serviceKey: service[0],
                                            }),
                                            line: {
                                              shape: "hv",
                                            },
                                          },
                                          ...(service[0] === "EA"
                                            ? [
                                                {
                                                  name: "Energy Price",
                                                  ...this.getChartData(v, "energyPrice"),
                                                  yaxis: "y2",
                                                  line: {
                                                    shape: "hv",
                                                  },
                                                },
                                              ]
                                            : []),
                                        ],
                                      },
                                    },
                                    {
                                      title: "Energy Allocation (MWh)",
                                      component: ChartTimeseries,
                                      props: {
                                        toImageButtonOptions: this.chartDownloadName(
                                          this.caseName,
                                          "SSE",
                                          "Energy Allocation (MWh)"
                                        ),
                                        layout: {
                                          yaxis: {
                                            title: "Energy [MWh]",
                                            range: [
                                              0,
                                              Math.max(
                                                ...this.SSEActiveProjectInput.storages[i]
                                                  .energy_capacity
                                              ) + 0.5,
                                            ],
                                            fixedrange: true,
                                          },
                                        },
                                        data: [
                                          {
                                            name: this.mapServiceKey(service[0]),
                                            ...this.getChartData(v, "eAlloc", {
                                              serviceKey: service[0],
                                            }),
                                            type: "bar",
                                          },
                                        ],
                                      },
                                    },
                                    {
                                      title: "Purchases & Sales ($)",
                                      component: ChartTimeseries,
                                      props: {
                                        toImageButtonOptions: this.chartDownloadName(
                                          this.caseName,
                                          "SSE",
                                          "Purchases & Sales ($)"
                                        ),
                                        layout: {
                                          barmode: "relative",
                                          ...(service[0] === "EA"
                                            ? {
                                                yaxis2: {
                                                  title: "Energy Price [$/MWh]",
                                                  overlaying: "y",
                                                  side: "right",
                                                },
                                              }
                                            : {}),
                                        },
                                        data: [
                                          {
                                            name: "Purchases [$]",
                                            ...this.getChartData(v, "purchases", {
                                              valueModifier: (val) => val * -1,
                                              serviceKey: service[0],
                                            }),
                                            type: "bar",
                                          },
                                          {
                                            name: "Sales [$]",
                                            ...this.getChartData(v, "sales", {
                                              serviceKey: service[0],
                                            }),
                                            type: "bar",
                                          },
                                          ...(service[0] === "EA"
                                            ? [
                                                {
                                                  name: "Energy Price",
                                                  ...this.getChartData(v, "energyPrice", {
                                                    duplicateLast: true,
                                                  }),
                                                  yaxis: "y2",
                                                  line: {
                                                    shape: "hvh",
                                                  },
                                                },
                                              ]
                                            : []),
                                        ],
                                      },
                                    },
                                  ],
                                },
                              },
                            })),
                          ...(v[0].R
                            ? Object.keys(v[0].R).map((reserveName) => ({
                                title: `Reserve: ${reserveName}`,
                                component: OutputSection,
                                props: {
                                  section: {
                                    title: "items",
                                    children: [
                                      {
                                        title: "Power Allocation (MW)",
                                        component: ChartTimeseries,
                                        props: {
                                          toImageButtonOptions: this.chartDownloadName(
                                            this.caseName,
                                            "SSE",
                                            "Power Allocation (MW)"
                                          ),
                                          layout: {
                                            yaxis: {
                                              title: "Power [MW]",
                                              range: [
                                                0,
                                                Math.max(
                                                  ...this.SSEActiveProjectInput.storages[i]
                                                    .power_capacity
                                                ) + 0.5,
                                              ],
                                              fixedrange: true,
                                            },
                                          },
                                          data: [
                                            {
                                              name: "",
                                              ...this.getReserveChartData(
                                                v,
                                                reserveName,
                                                "powAlloc"
                                              ),
                                              type: "bar",
                                            },
                                          ],
                                        },
                                      },
                                      {
                                        title: "Power Nomination (MW)",
                                        component: ChartTimeseries,
                                        props: {
                                          toImageButtonOptions: this.chartDownloadName(
                                            this.caseName,
                                            "SSE",
                                            "Power Nomination (MW)"
                                          ),
                                          layout: {
                                            yaxis: {
                                              title: "Power [MW]",
                                              range: [
                                                -Math.max(
                                                  ...this.SSEActiveProjectInput.storages[i]
                                                    .power_capacity
                                                ),
                                                Math.max(
                                                  ...this.SSEActiveProjectInput.storages[i]
                                                    .power_capacity
                                                ),
                                              ],
                                              fixedrange: true,
                                            },
                                          },
                                          data: [
                                            {
                                              name: "",
                                              ...this.getReserveChartData(v, reserveName, "powNom"),
                                              line: {
                                                shape: "hv",
                                              },
                                            },
                                          ],
                                        },
                                      },
                                      {
                                        title: "Energy Allocation (MWh)",
                                        component: ChartTimeseries,
                                        props: {
                                          toImageButtonOptions: this.chartDownloadName(
                                            this.caseName,
                                            "SSE",
                                            "Energy Allocation (MWh)"
                                          ),
                                          layout: {
                                            yaxis: {
                                              title: "Energy [MWh]",
                                              range: [
                                                0,
                                                Math.max(
                                                  ...this.SSEActiveProjectInput.storages[i]
                                                    .energy_capacity
                                                ),
                                              ],
                                              fixedrange: true,
                                            },
                                          },
                                          data: [
                                            {
                                              name: "",
                                              ...this.getReserveChartData(v, reserveName, "eAlloc"),
                                            },
                                          ],
                                        },
                                      },
                                      {
                                        title: "Purchases & Sales ($)",
                                        component: ChartTimeseries,
                                        props: {
                                          toImageButtonOptions: this.chartDownloadName(
                                            this.caseName,
                                            "SSE",
                                            "Purchases & Sales ($)"
                                          ),
                                          layout: {
                                            barmode: "relative",
                                          },
                                          data: [
                                            {
                                              name: "Purchases [$]",
                                              ...this.getReserveChartData(
                                                v,
                                                reserveName,
                                                "purchases",
                                                {
                                                  valueModifier: (val) => val * -1,
                                                }
                                              ),
                                              type: "bar",
                                            },
                                            {
                                              name: "Sales [$]",
                                              ...this.getReserveChartData(v, reserveName, "sales"),
                                              type: "bar",
                                            },
                                          ],
                                        },
                                      },
                                    ],
                                  },
                                },
                              }))
                            : []),
                        ],
                      },
                    },
                  },
                ],
              }))
            : [];
        } else if (viewMode === "daily") {
          panelChildren = Object.entries(this.SSEDailyData.storages || {}).map(([key, v], i) => ({
            title: key,
            children: [
              {
                title: `storage-${i}`,
                component: OutputSection,
                props: {
                  section: {
                    title: `storage-${i}-items`,
                    children: [
                      {
                        title: "Daily P&L",
                        component: Plotly,
                        props: {
                          toImageButtonOptions: this.chartDownloadName(
                            this.caseName,
                            "SSE",
                            "Daily P&L"
                          ),
                          layout: {
                            images: [this.acelerexLogo],
                            yaxis: {
                              title: "$",
                            },
                            xaxis: {
                              tickformat:
                                this.SSEToolbarSettings.selectedYear.length >= 2 ? "%d/%m" : "",
                            },
                            barmode: "relative",
                          },
                          data: this.SSEToolbarSettings.selectedYear.length
                            ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                name: year,
                                ...this.getChartData(v, "pL", {
                                  dataType: "daily",
                                  selectedYear: year,
                                }),
                                line: {
                                  shape: "hv",
                                  color: "#000000",
                                },
                                type: "bar",
                              }))
                            : [
                                {
                                  name: "P&L",
                                  ...this.getChartData(v, "pL", {
                                    dataType: "daily",
                                    selectedYear: Object.keys(v)[0],
                                  }),
                                  line: {
                                    shape: "hv",
                                    color: "#000000",
                                  },
                                  type: "bar",
                                },
                              ],
                        },
                      },
                      {
                        title: "Daily P&L Duration Curve",
                        component: Plotly,
                        props: {
                          toImageButtonOptions: this.chartDownloadName(
                            this.caseName,
                            "SSE",
                            "Daily P&L Duration Curve"
                          ),
                          layout: {
                            images: [this.acelerexLogo],
                            yaxis: {
                              title: "$",
                              fixedrange: true,
                            },
                            xaxis: {
                              type:
                                this.SSEToolbarSettings.selectedYear.length >= 2 ? "" : "category",
                            },
                            barmode: "relative",
                          },
                          data: this.SSEToolbarSettings.selectedYear.length
                            ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                name: year,
                                ...this.getChartData(v, "pL", {
                                  dataType: "daily",
                                  selectedYear: year,
                                  sort: true,
                                }),
                                line: {
                                  shape: "hv",
                                  color: "#000000",
                                },
                                type: "bar",
                              }))
                            : [
                                {
                                  name: "P&L",
                                  ...this.getChartData(v, "pL", {
                                    dataType: "daily",
                                    selectedYear: Object.keys(v)[0],
                                    sort: true,
                                  }),
                                  line: {
                                    shape: "hv",
                                    color: "#000000",
                                  },
                                  type: "bar",
                                },
                              ],
                        },
                      },
                      {
                        title: "Daily P&L/Cycle",
                        component: Plotly,
                        props: {
                          toImageButtonOptions: this.chartDownloadName(
                            this.caseName,
                            "SSE",
                            "Daily P&L/Cycle"
                          ),
                          layout: {
                            images: [this.acelerexLogo],
                            yaxis: {
                              title: "",
                              fixedrange: true,
                            },
                            xaxis: {
                              tickformat:
                                this.SSEToolbarSettings.selectedYear.length >= 2 ? "%d/%m" : "",
                            },

                            barmode: "relative",
                          },
                          data: this.SSEToolbarSettings.selectedYear.length
                            ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                name: year,
                                ...this.getChartData(v, "pLPerCycles", {
                                  dataType: "daily",
                                  selectedYear: year,
                                }),
                                line: {
                                  shape: "hv",
                                  color: "#000000",
                                },
                                type: "bar",
                              }))
                            : [
                                {
                                  name: "P&L/Cycle",
                                  ...this.getChartData(v, "pLPerCycles", {
                                    dataType: "daily",
                                    selectedYear: Object.keys(v)[0],
                                  }),
                                  line: {
                                    shape: "hv",
                                    color: "#000000",
                                  },
                                  type: "bar",
                                },
                              ],
                        },
                      },
                      {
                        title: "Daily P&L/Cycle Duration Curve",
                        component: Plotly,
                        props: {
                          toImageButtonOptions: this.chartDownloadName(
                            this.caseName,
                            "SSE",
                            "Daily P&L/Cycle Duration Curve"
                          ),
                          layout: {
                            images: [this.acelerexLogo],
                            yaxis: {
                              title: "$",
                              fixedrange: true,
                            },
                            xaxis: {
                              type:
                                this.SSEToolbarSettings.selectedYear.length >= 2 ? "" : "category",
                            },
                            barmode: "relative",
                          },
                          data: this.SSEToolbarSettings.selectedYear.length
                            ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                name: year,
                                ...this.getChartData(v, "pLPerCycles", {
                                  dataType: "daily",
                                  selectedYear: year,
                                  sort: true,
                                }),
                                line: {
                                  shape: "hv",
                                  color: "#000000",
                                },
                                type: "bar",
                              }))
                            : [
                                {
                                  name: "P&L/Cycle",
                                  ...this.getChartData(v, "pLPerCycles", {
                                    dataType: "daily",
                                    selectedYear: Object.keys(v)[0],
                                    sort: true,
                                  }),
                                  line: {
                                    shape: "hv",
                                    color: "#000000",
                                  },
                                  type: "bar",
                                },
                              ],
                        },
                      },
                      {
                        title: "Daily Energy Purchases",
                        component: Plotly,
                        props: {
                          toImageButtonOptions: this.chartDownloadName(
                            this.caseName,
                            "SSE",
                            "Daily Energy Purchases"
                          ),
                          layout: {
                            images: [this.acelerexLogo],
                            yaxis: {
                              title: "$-d",
                              fixedrange: true,
                            },
                            xaxis: {
                              tickformat:
                                this.SSEToolbarSettings.selectedYear.length >= 2 ? "%d/%m" : "",
                            },

                            barmode: "relative",
                          },
                          data: this.SSEToolbarSettings.selectedYear.length
                            ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                name: year,
                                ...this.getChartData(v, "totalEnergyPurchases", {
                                  dataType: "daily",
                                  selectedYear: year,
                                }),
                                line: {
                                  shape: "hv",
                                  color: "#000000",
                                },
                                type: "bar",
                              }))
                            : [
                                {
                                  name: "Energy Purchases [$-d]",
                                  ...this.getChartData(v, "totalEnergyPurchases", {
                                    dataType: "daily",
                                    selectedYear: Object.keys(v)[0],
                                  }),
                                  line: {
                                    shape: "hv",
                                    color: "#000000",
                                  },
                                  type: "bar",
                                },
                              ],
                        },
                      },
                      {
                        title: "Daily Energy Sales",
                        component: Plotly,
                        props: {
                          toImageButtonOptions: this.chartDownloadName(
                            this.caseName,
                            "SSE",
                            "Daily Energy Sales"
                          ),
                          layout: {
                            images: [this.acelerexLogo],
                            yaxis: {
                              title: "$-d",
                              fixedrange: true,
                            },
                            xaxis: {
                              tickformat:
                                this.SSEToolbarSettings.selectedYear.length >= 2 ? "%d/%m" : "",
                            },

                            barmode: "relative",
                          },
                          data: this.SSEToolbarSettings.selectedYear.length
                            ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                name: year,
                                ...this.getChartData(v, "totalEnergySales", {
                                  dataType: "daily",
                                  selectedYear: year,
                                }),
                                line: {
                                  shape: "hv",
                                  color: "#000000",
                                },
                                type: "bar",
                              }))
                            : [
                                {
                                  name: "Energy Sales [$-d]",
                                  ...this.getChartData(v, "totalEnergySales", {
                                    dataType: "daily",
                                    selectedYear: Object.keys(v)[0],
                                  }),
                                  line: {
                                    shape: "hv",
                                    color: "#000000",
                                  },
                                  type: "bar",
                                },
                              ],
                        },
                      },
                      {
                        title: "Daily Energy Throughput",
                        component: Plotly,
                        props: {
                          toImageButtonOptions: this.chartDownloadName(
                            this.caseName,
                            "SSE",
                            "Daily Energy Throughput"
                          ),
                          layout: {
                            images: [this.acelerexLogo],
                            yaxis: {
                              title: "MWh-d",
                              fixedrange: true,
                            },
                            xaxis: {
                              tickformat:
                                this.SSEToolbarSettings.selectedYear.length >= 2 ? "%d/%m" : "",
                            },

                            barmode: "relative",
                          },
                          data: this.SSEToolbarSettings.selectedYear.length
                            ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                name: year,
                                ...this.getChartData(v, "energyThroughput", {
                                  dataType: "daily",
                                  selectedYear: year,
                                }),
                                line: {
                                  shape: "hv",
                                  color: "#000000",
                                },
                                type: "bar",
                              }))
                            : [
                                {
                                  name: "Energy Throughput [MWh-d]",
                                  ...this.getChartData(v, "energyThroughput", {
                                    dataType: "daily",
                                    selectedYear: Object.keys(v)[0],
                                  }),
                                  line: {
                                    shape: "hv",
                                    color: "#000000",
                                  },
                                  type: "bar",
                                },
                              ],
                        },
                      },
                      {
                        title: "Daily Cycles",
                        component: Plotly,
                        props: {
                          toImageButtonOptions: this.chartDownloadName(
                            this.caseName,
                            "SSE",
                            "Daily Cycles"
                          ),
                          layout: {
                            images: [this.acelerexLogo],
                            yaxis: {
                              title: "",
                              fixedrange: true,
                            },
                            xaxis: {
                              tickformat:
                                this.SSEToolbarSettings.selectedYear.length >= 2 ? "%d/%m" : "",
                            },

                            barmode: "relative",
                          },
                          data: this.SSEToolbarSettings.selectedYear.length
                            ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                name: year,
                                ...this.getChartData(v, "cycles", {
                                  dataType: "daily",
                                  selectedYear: year,
                                }),
                                line: {
                                  shape: "hv",
                                  color: "#000000",
                                },
                                type: "bar",
                              }))
                            : [
                                {
                                  name: "Cycles",
                                  ...this.getChartData(v, "cycles", {
                                    dataType: "daily",
                                    selectedYear: Object.keys(v)[0],
                                  }),
                                  line: {
                                    shape: "hv",
                                    color: "#000000",
                                  },
                                  type: "bar",
                                },
                              ],
                        },
                      },
                      ...Object.entries(this.SSEActiveProjectInput.storages[i].services)
                        .filter((entry) => entry[1].active)
                        .map(([serviceKey, value]) => [
                          this.mapServiceToOutputKey(serviceKey),
                          value,
                        ])
                        .filter((service) => service[0] !== undefined)
                        .map((service) => ({
                          title: this.mapServiceKey(service[0]),
                          component: OutputSection,
                          props: {
                            section: {
                              title: "items",
                              children: [
                                {
                                  title: "Purchases & Sales ($)",
                                  component: Plotly,
                                  props: {
                                    toImageButtonOptions: this.chartDownloadName(
                                      this.caseName,
                                      "SSE",
                                      `${this.mapServiceKey(
                                        service[0]
                                      )} Daily Purchases & Sales ($)`
                                    ),
                                    layout: {
                                      images: [this.acelerexLogo],
                                      barmode: "relative",
                                      xaxis: {
                                        tickformat:
                                          this.SSEToolbarSettings.selectedYear.length >= 2
                                            ? "%d/%m"
                                            : "",
                                      },
                                    },
                                    data: [
                                      {
                                        name: "Purchases [$]",
                                        ...this.getChartData(v, "purchases", {
                                          valueModifier: (val) => val * -1,
                                          serviceKey: service[0],
                                          dataType: "daily",
                                          selectedYear: Object.keys(v)[0],
                                        }),
                                        type: "bar",
                                      },
                                      {
                                        name: "Sales [$]",
                                        ...this.getChartData(v, "sales", {
                                          serviceKey: service[0],
                                          dataType: "daily",
                                          selectedYear: Object.keys(v)[0],
                                        }),
                                        type: "bar",
                                      },
                                    ],
                                  },
                                },
                                {
                                  title: "Power Allocation (MW)",
                                  component: Plotly,
                                  props: {
                                    toImageButtonOptions: this.chartDownloadName(
                                      this.caseName,
                                      "SSE",
                                      `${this.mapServiceKey(
                                        service[0]
                                      )} Daily Power Allocation (MW)`
                                    ),
                                    layout: {
                                      images: [this.acelerexLogo],
                                      yaxis: {
                                        title: "Power [MW]",
                                        range: [
                                          0,
                                          Math.max(
                                            ...this.SSEActiveProjectInput.storages[i].power_capacity
                                          ) + 0.5,
                                        ],
                                        fixedrange: true,
                                      },
                                      xaxis: {
                                        tickformat:
                                          this.SSEToolbarSettings.selectedYear.length >= 2
                                            ? "%d/%m"
                                            : "",
                                      },
                                    },
                                    data: [
                                      {
                                        name: this.mapServiceKey(service[0]),
                                        ...this.getChartData(v, "powerAllocation", {
                                          serviceKey: service[0],
                                          dataType: "daily",
                                          selectedYear: Object.keys(v)[0],
                                        }),
                                        type: "bar",
                                      },
                                    ],
                                  },
                                },
                                {
                                  title: "Energy Allocation (MWh)",
                                  component: Plotly,
                                  props: {
                                    toImageButtonOptions: this.chartDownloadName(
                                      this.caseName,
                                      "SSE",
                                      `${this.mapServiceKey(
                                        service[0]
                                      )} Daily Energy Allocation (MWh)`
                                    ),
                                    layout: {
                                      images: [this.acelerexLogo],
                                      yaxis: {
                                        title: "Energy [MWh]",
                                        range: [
                                          0,
                                          Math.max(
                                            ...this.SSEActiveProjectInput.storages[i]
                                              .energy_capacity
                                          ) + 0.5,
                                        ],
                                        fixedrange: true,
                                        xaxis: {
                                          tickformat:
                                            this.SSEToolbarSettings.selectedYear.length >= 2
                                              ? "%d/%m"
                                              : "",
                                        },
                                      },
                                    },
                                    data: [
                                      {
                                        name: this.mapServiceKey(service[0]),
                                        ...this.getChartData(v, "energyAllocation", {
                                          serviceKey: service[0],
                                          dataType: "daily",
                                          selectedYear: Object.keys(v)[0],
                                        }),
                                        type: "bar",
                                      },
                                    ],
                                  },
                                },
                              ],
                            },
                          },
                        })),
                      ...(Object.values(v)[0][0].R
                        ? Object.keys(Object.values(v)[0][0].R).map((reserveName) => ({
                            title: `Reserve: ${reserveName}`,
                            component: OutputSection,
                            props: {
                              section: {
                                title: "items",
                                children: [
                                  {
                                    title: "Purchases & Sales ($)",
                                    component: Plotly,
                                    props: {
                                      toImageButtonOptions: this.chartDownloadName(
                                        this.caseName,
                                        "SSE",
                                        `${reserveName} Daily Purchases & Sales ($)`
                                      ),
                                      layout: {
                                        images: [this.acelerexLogo],
                                        barmode: "relative",
                                        xaxis: {
                                          tickformat:
                                            this.SSEToolbarSettings.selectedYear.length >= 2
                                              ? "%d/%m"
                                              : "",
                                        },
                                      },
                                      data: [
                                        {
                                          name: "Purchases [$]",
                                          ...this.getReserveChartData(v, reserveName, "purchases", {
                                            valueModifier: (val) => val * -1,
                                            dataType: "daily",
                                            selectedYear: Object.keys(v)[0],
                                          }),
                                          type: "bar",
                                        },
                                        {
                                          name: "Sales [$]",
                                          ...this.getReserveChartData(v, reserveName, "sales", {
                                            dataType: "daily",
                                            selectedYear: Object.keys(v)[0],
                                          }),
                                          type: "bar",
                                        },
                                      ],
                                    },
                                  },
                                  {
                                    title: "Power Allocation (MW)",
                                    component: Plotly,
                                    props: {
                                      toImageButtonOptions: this.chartDownloadName(
                                        this.caseName,
                                        "SSE",
                                        `${reserveName} Daily Power Allocation (MW)`
                                      ),
                                      layout: {
                                        images: [this.acelerexLogo],
                                        yaxis: {
                                          title: "Power [MW]",
                                          range: [
                                            0,
                                            Math.max(
                                              ...this.SSEActiveProjectInput.storages[i]
                                                .power_capacity
                                            ) + 0.5,
                                          ],
                                          fixedrange: true,
                                          xaxis: {
                                            tickformat:
                                              this.SSEToolbarSettings.selectedYear.length >= 2
                                                ? "%d/%m"
                                                : "",
                                          },
                                        },
                                      },
                                      data: [
                                        {
                                          ...this.getReserveChartData(
                                            v,
                                            reserveName,
                                            "powerAllocation",
                                            {
                                              dataType: "daily",
                                              selectedYear: Object.keys(v)[0],
                                            }
                                          ),
                                          type: "bar",
                                        },
                                      ],
                                    },
                                  },
                                  {
                                    title: "Energy Allocation (MWh)",
                                    component: Plotly,
                                    props: {
                                      toImageButtonOptions: this.chartDownloadName(
                                        this.caseName,
                                        "SSE",
                                        `${reserveName} Daily Energy Allocation (MWh)`
                                      ),
                                      layout: {
                                        images: [this.acelerexLogo],
                                        yaxis: {
                                          title: "Energy [MWh]",
                                          range: [
                                            0,
                                            Math.max(
                                              ...this.SSEActiveProjectInput.storages[i]
                                                .energy_capacity
                                            ) + 0.5,
                                          ],
                                          fixedrange: true,
                                          xaxis: {
                                            tickformat:
                                              this.SSEToolbarSettings.selectedYear.length >= 2
                                                ? "%d/%m"
                                                : "",
                                          },
                                        },
                                      },
                                      data: [
                                        {
                                          ...this.getReserveChartData(
                                            v,
                                            reserveName,
                                            "energyAllocation",
                                            {
                                              dataType: "daily",
                                              selectedYear: Object.keys(v)[0],
                                            }
                                          ),
                                          type: "bar",
                                        },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          }))
                        : []),
                    ],
                  },
                },
              },
            ],
          }));
        }
        page.sections.push({
          title: "Storages",
          panel: this.SSEResourcesItems.storages.map((_el, i) => i),
          children: panelChildren,
        });
      }

      if (
        this.SSEToolbarSettings.markets &&
        this.SSEToolbarSettings.markets.length &&
        this.SSEDashboardToolbarSettings.viewMode === "single"
      ) {
        const { viewMode } = this.SSEToolbarSettings;
        let panelChildren;
        if (viewMode === "annual") {
          panelChildren = Object.values(
            this.SSEYearlyData[Object.keys(this.SSEYearlyData)[0]].markets
          ).map((v) => ({
            title: v.name,
            children: [
              {
                title: `market-${v.id}`,
                component: DataTableYearly,
                props: {
                  year_start: this.SSEStartYear,
                  items: [
                    {
                      name: "Annual Purchases ($-yr)",
                      value: this.getAnnualValue("markets", v.id, "purchases"),
                    },
                    {
                      name: "Annual Imports (MWh-yr)",
                      value: this.getAnnualValue("markets", v.id, "imports"),
                    },
                    {
                      name: "Annual Sales ($-yr)",
                      value: this.getAnnualValue("markets", v.id, "sales"),
                    },
                    {
                      name: "Annual Exports (MWh-yr)",
                      value: this.getAnnualValue("markets", v.id, "exports"),
                    },
                  ],
                },
              },
            ],
          }));
        } else if (viewMode === "monthly") {
          panelChildren = this.SSEMonthlyData.markets
            ? Object.entries(this.SSEMonthlyData.markets || {}).map(([key, v], i) => ({
                title: key,
                children: [
                  {
                    title: `market-${i}`,
                    component: OutputSection,
                    props: {
                      section: {
                        title: `market-${i}-items`,
                        children: [
                          {
                            title: "Monthly Average Energy Price ($/MWh)",
                            component: Plotly,
                            props: {
                              layout: this.monthlyChartLayout("Energy Price [$/MWh]"),
                              data: this.SSEToolbarSettings.selectedYear.map((year) => ({
                                name: year,
                                ...this.getChartData(v, "energyPrice", {
                                  dataType: "monthly",
                                  selectedYear: year,
                                }),
                              })),
                            },
                          },
                          {
                            title: "Monthly Imports (MWh-month)",
                            component: Plotly,
                            props: {
                              layout: this.monthlyChartLayout("Imports [MWh-month]"),
                              data: this.SSEToolbarSettings.selectedYear.map((year) => ({
                                name: year,
                                ...this.getChartData(v, "imports", {
                                  dataType: "monthly",
                                  selectedYear: year,
                                }),
                                type: "bar",
                              })),
                            },
                          },
                          {
                            title: "Monthly Exports (MWh-month)",
                            component: Plotly,
                            props: {
                              layout: this.monthlyChartLayout("Exports [MWh-month]"),
                              data: this.SSEToolbarSettings.selectedYear.map((year) => ({
                                name: year,
                                ...this.getChartData(v, "exports", {
                                  dataType: "monthly",
                                  selectedYear: year,
                                }),
                                type: "bar",
                              })),
                            },
                          },
                          {
                            title: "Monthly Sales ($-month)",
                            component: Plotly,
                            props: {
                              layout: this.monthlyChartLayout("Sales [$]"),
                              data: this.SSEToolbarSettings.selectedYear.map((year) => ({
                                name: year,
                                ...this.getChartData(v, "sales", {
                                  dataType: "monthly",
                                  selectedYear: year,
                                }),
                                type: "bar",
                              })),
                            },
                          },
                          {
                            title: "Monthly Purchases ($-month))",
                            component: Plotly,
                            props: {
                              layout: this.monthlyChartLayout("Purchases [$]"),
                              data: this.SSEToolbarSettings.selectedYear.map((year) => ({
                                name: year,
                                ...this.getChartData(v, "purchases", {
                                  dataType: "monthly",
                                  selectedYear: year,
                                }),
                                type: "bar",
                              })),
                            },
                          },
                        ],
                      },
                    },
                  },
                ],
              }))
            : [];
        } else if (viewMode === "daily") {
          panelChildren = Object.entries(this.SSEDailyData.markets || {}).map(([key, v], i) => ({
            title: key,
            children: [
              {
                title: `market-${i}`,
                component: OutputSection,
                props: {
                  section: {
                    title: `market-${i}-items`,
                    children: [
                      {
                        title: "Daily Average Energy Price ($/MWh)",
                        component: Plotly,
                        props: {
                          layout: this.dailyChartLayout("Energy Price [$/MWh]"),
                          data: this.SSEToolbarSettings.selectedYear.map((year) => ({
                            name: year,
                            ...this.getChartData(v, "energyPrice", {
                              dataType: "daily",
                              selectedYear: year,
                            }),
                          })),
                        },
                      },
                      {
                        title: "Daily Imports (MWh-day)",
                        component: Plotly,
                        props: {
                          layout: this.dailyChartLayout("Imports [MWh-day]"),
                          data: this.SSEToolbarSettings.selectedYear.map((year) => ({
                            name: year,
                            ...this.getChartData(v, "imports", {
                              dataType: "daily",
                              selectedYear: year,
                            }),
                            type: "bar",
                          })),
                        },
                      },
                      {
                        title: "Daily Exports (MWh-day)",
                        component: Plotly,
                        props: {
                          layout: this.dailyChartLayout("Exports [MWh-day]"),
                          data: this.SSEToolbarSettings.selectedYear.map((year) => ({
                            name: year,
                            ...this.getChartData(v, "exports", {
                              dataType: "daily",
                              selectedYear: year,
                            }),
                            type: "bar",
                          })),
                        },
                      },
                      {
                        title: "Daily Sales ($-day)",
                        component: Plotly,
                        props: {
                          layout: this.dailyChartLayout("Sales [$]"),
                          data: this.SSEToolbarSettings.selectedYear.map((year) => ({
                            name: year,
                            ...this.getChartData(v, "sales", {
                              dataType: "daily",
                              selectedYear: year,
                            }),
                            type: "bar",
                          })),
                        },
                      },
                      {
                        title: "Daily Purchases ($-day))",
                        component: Plotly,
                        props: {
                          layout: this.dailyChartLayout("Purchases [$]"),
                          data: this.SSEToolbarSettings.selectedYear.map((year) => ({
                            name: year,
                            ...this.getChartData(v, "purchases", {
                              dataType: "daily",
                              selectedYear: year,
                            }),
                            type: "bar",
                          })),
                        },
                      },
                    ],
                  },
                },
              },
            ],
          }));
        } else if (viewMode === "timeSeries") {
          panelChildren = this.SSETimeseriesData.markets
            ? Object.entries(this.SSETimeseriesData.markets).map(([key, v], i) => ({
                title: key,
                children: [
                  {
                    title: `market-view-${i}`,
                    component: OutputSection,
                    props: {
                      panel: [0],
                      section: {
                        title: "items",
                        children: [
                          {
                            title: "Purchases & Sales",
                            component: ChartTimeseries,
                            props: {
                              toImageButtonOptions: this.chartDownloadName(
                                this.caseName,
                                "SSE",
                                "Purchases & Sales"
                              ),
                              layout: {
                                barmode: "relative",
                                yaxis: {
                                  title: "[$]",
                                },
                                annotations: [
                                  {
                                    xref: "paper",
                                    x: 0.01,
                                    y: 100,
                                    text: "Sales",
                                    yanchor: "bottom",
                                    showarrow: false,
                                    bgcolor: "#ffffff",
                                    textangle: -90,
                                  },
                                  {
                                    xref: "paper",
                                    x: 0.01,
                                    y: -100,
                                    text: "Purchases",
                                    yanchor: "top",
                                    showarrow: false,
                                    bgcolor: "#ffffff",
                                    textangle: -90,
                                  },
                                ],
                              },
                              data: [
                                {
                                  name: "Purchases [$]",
                                  ...this.getChartData(v, "purchases", {
                                    valueModifier: (val) => val * -1,
                                  }),
                                  type: "bar",
                                },
                                {
                                  name: "Sales [$]",
                                  ...this.getChartData(v, "sales", {}),
                                  type: "bar",
                                },
                              ],
                            },
                          },
                          {
                            title: "Imports & Exports",
                            component: ChartTimeseries,
                            props: {
                              toImageButtonOptions: this.chartDownloadName(
                                this.caseName,
                                "SSE",
                                "Imports & Exports"
                              ),
                              layout: {
                                barmode: "relative",
                                yaxis: {
                                  title: "[MW]",
                                },
                                yaxis2: {
                                  title: "[$/MWh]",
                                  overlaying: "y",
                                  side: "right",
                                },
                                annotations: [
                                  {
                                    xref: "paper",
                                    x: 0.01,
                                    y: 1,
                                    text: "Exports",
                                    yanchor: "bottom",
                                    showarrow: false,
                                    bgcolor: "#ffffff",
                                    textangle: -90,
                                  },
                                  {
                                    xref: "paper",
                                    x: 0.01,
                                    y: -1,
                                    text: "Imports",
                                    yanchor: "top",
                                    showarrow: false,
                                    bgcolor: "#ffffff",
                                    textangle: -90,
                                  },
                                ],
                              },
                              data: [
                                {
                                  name: "Imports [MW]",
                                  ...this.getChartData(v, "imports", {
                                    valueModifier: (val) => val * -1,
                                  }),
                                  type: "bar",
                                },
                                {
                                  name: "Exports [MW]",
                                  ...this.getChartData(v, "exports", {}),
                                  type: "bar",
                                },
                                {
                                  name: "Energy Price [$/MWh]",
                                  ...this.getChartData(v, "energyPrice", {}),
                                  yaxis: "y2",
                                  mode: "lines",
                                  line: {
                                    shape: "hvh",
                                  },
                                },
                              ],
                            },
                          },
                        ],
                      },
                    },
                  },
                ],
              }))
            : [];
        }
        page.sections.push({
          title: "Markets",
          panel: this.SSEResourcesItems.markets.map((el, i) => i),
          children: panelChildren,
        });
      }
      if (
        this.SSEToolbarSettings.thermal_generators &&
        this.SSEToolbarSettings.thermal_generators.length &&
        this.SSEDashboardToolbarSettings.viewMode === "single"
      ) {
        const { viewMode } = this.SSEToolbarSettings;
        let panelChildren;
        if (viewMode === "annual") {
          panelChildren = Object.values(
            this.SSEYearlyData[Object.keys(this.SSEYearlyData)[0]].thermal_generators
          ).map((v) => ({
            title: v.name,
            children: [
              {
                title: `generator-${v.id}`,
                component: OutputSection,
                props: {
                  panel: [0],
                  section: {
                    title: `generator-${v.id}-items`,
                    children: [
                      {
                        title: "Summary",
                        component: DataTableYearly,
                        props: {
                          year_start: this.SSEStartYear,
                          items: [
                            {
                              name: "Annual Generation (MWh-yr)",
                              value: this.getAnnualValue("thermal_generators", v.id, "generation"),
                            },
                            {
                              name: "Annual Sales ($-yr)",
                              value: this.getAnnualValue("thermal_generators", v.id, "sales"),
                            },
                            {
                              name: "Annual P&L ($-yr)",
                              value: this.getAnnualValue("thermal_generators", v.id, "pL"),
                            },
                          ],
                        },
                      },
                      {
                        title: "P&L ($)",
                        component: Plotly,
                        props: {
                          toImageButtonOptions: this.chartDownloadName(
                            this.caseName,
                            "SSE",
                            "P&L ($)"
                          ),
                          layout: {
                            images: [this.acelerexLogo],
                            yaxis: {
                              title: "P&L [$]",
                            },
                          },
                          data: this.getResourceCompChartData(
                            v.id,
                            "thermal_generators",
                            "storages",
                            "pL"
                          ),
                        },
                      },
                    ],
                  },
                },
              },
            ],
          }));
        } else if (viewMode === "monthly") {
          panelChildren = Object.entries(this.SSEMonthlyData.thermal_generators || {}).map(
            ([key, v], i) => ({
              title: key,
              children: [
                {
                  title: `thermalGen-${i}`,
                  component: OutputSection,
                  props: {
                    section: {
                      title: `thermalGen-${i}-items`,
                      children: [
                        {
                          title: "Monthly Generation (MWh)",
                          component: Plotly,
                          props: {
                            layout: {
                              images: [this.acelerexLogo],
                              yaxis: {
                                title: "Generation [MWh]",
                                fixedrange: true,
                              },
                              xaxis: {
                                tickformat:
                                  this.SSEToolbarSettings.selectedYear.length >= 2 ? "%m" : "",
                              },
                            },
                            data: this.SSEToolbarSettings.selectedYear.length
                              ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                  name: year,
                                  ...this.getChartData(v, "totalGeneration", {
                                    dataType: "monthly",
                                    selectedYear: year,
                                  }),
                                  type: "bar",
                                }))
                              : [
                                  {
                                    ...this.getChartData(v, "totalGeneration", {
                                      dataType: "monthly",
                                      selectedYear: Object.keys(v)[0],
                                    }),
                                    type: "bar",
                                  },
                                ],
                          },
                        },
                      ],
                    },
                  },
                },
              ],
            })
          );
        } else if (viewMode === "daily") {
          panelChildren = Object.entries(this.SSEDailyData.thermal_generators || {}).map(
            ([key, v], i) => ({
              title: key,
              children: [
                {
                  title: `thermalGen-${i}`,
                  component: OutputSection,
                  props: {
                    section: {
                      title: `thermalGen-${i}-items`,
                      children: [
                        {
                          title: "Daily Generation (MWh)",
                          component: Plotly,
                          props: {
                            layout: {
                              images: [this.acelerexLogo],
                              yaxis: {
                                title: "Generation [MWh]",
                                fixedrange: true,
                              },
                              xaxis: {
                                tickformat:
                                  this.SSEToolbarSettings.selectedYear.length >= 2 ? "%d/%m" : "",
                              },
                            },
                            data: this.SSEToolbarSettings.selectedYear.length
                              ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                  name: year,
                                  ...this.getChartData(v, "totalGeneration", {
                                    dataType: "daily",
                                    selectedYear: year,
                                  }),
                                  type: "bar",
                                }))
                              : [
                                  {
                                    ...this.getChartData(v, "totalGeneration", {
                                      dataType: "daily",
                                      selectedYear: Object.keys(v)[0],
                                    }),
                                    type: "bar",
                                  },
                                ],
                          },
                        },
                      ],
                    },
                  },
                },
              ],
            })
          );
        } else if (viewMode === "timeSeries") {
          panelChildren = this.SSETimeseriesData.thermal_generators
            ? Object.entries(this.SSETimeseriesData.thermal_generators).map(([key, v], i) => ({
                title: key,
                children: [
                  {
                    title: `thermalGen-${i}`,
                    component: OutputSection,
                    props: {
                      panel: [0],
                      section: {
                        title: `thermalGen-${i}-items`,
                        children: [
                          {
                            title: "Generation (MWh)",
                            component: ChartTimeseries,
                            props: {
                              layout: {
                                yaxis: {
                                  title: "Generation [MWh]",
                                },
                                yaxis2: {
                                  title: "[$/MWh]",
                                  overlaying: "y",
                                  side: "right",
                                },
                              },
                              data: [
                                {
                                  name: "Generation [MWh]",
                                  ...this.getChartData(v, "generation", {}),
                                  type: "bar",
                                },
                                {
                                  name: "Energy Price [$/MWh]",
                                  ...this.getChartData(v, "energyPrice", { duplicateLast: true }),
                                  yaxis: "y2",
                                  mode: "lines",
                                  line: {
                                    shape: "hvh",
                                  },
                                },
                                {
                                  name: "SRMC [$/MWh]",
                                  ...this.getChartData(v, "srmc", { duplicateLast: true }),
                                  yaxis: "y2",
                                  mode: "lines",
                                  line: {
                                    shape: "hvh",
                                  },
                                },
                              ],
                            },
                          },
                          {
                            title: "P&L ($)",
                            component: ChartTimeseries,
                            props: {
                              layout: {
                                yaxis: {
                                  title: "P&L [MWh]",
                                },
                                yaxis2: {
                                  title: "[$/MWh]",
                                  overlaying: "y",
                                  side: "right",
                                },
                              },
                              data: [
                                {
                                  name: "P&L [$]",
                                  ...this.getChartData(v, "pL", {}),
                                  type: "bar",
                                },
                                {
                                  name: "Energy Price [$/MWh]",
                                  ...this.getChartData(v, "energyPrice", { duplicateLast: true }),
                                  yaxis: "y2",
                                  mode: "lines",
                                  line: {
                                    shape: "hvh",
                                  },
                                },
                              ],
                            },
                          },
                        ],
                      },
                    },
                  },
                ],
              }))
            : [];
        }
        page.sections.push({
          title: "Thermal Generators",
          panel: this.SSEResourcesItems.thermal_generators.map((el, i) => i),
          children: panelChildren || [],
        });
      }

      if (
        this.SSEToolbarSettings.renewable_generators &&
        this.SSEToolbarSettings.renewable_generators.length &&
        this.SSEDashboardToolbarSettings.viewMode === "single"
      ) {
        const sectionChildren = {
          annual: Object.values(
            this.SSEYearlyData[Object.keys(this.SSEYearlyData)[0]].renewable_generators
          ).map((v) => ({
            title: v.name,
            children: [
              {
                title: `generator-${v.id}`,
                component: OutputSection,
                props: {
                  panel: [0],
                  section: {
                    title: `generator-${v.id}-items`,
                    children: [
                      {
                        title: "Summary",
                        component: DataTableYearly,
                        props: {
                          year_start: this.SSEStartYear,
                          items: [
                            {
                              name: "Annual Generation (MWh-yr)",
                              value: this.getAnnualValue(
                                "renewable_generators",
                                v.id,
                                "generation"
                              ),
                            },
                            {
                              name: "Annual Curtailment (%-yr)",
                              value: this.getAnnualValue(
                                "renewable_generators",
                                v.id,
                                "curtailment"
                              ),
                            },
                            {
                              name: "Annual Available Resources (MWh-yr)",
                              value: this.getAnnualValue(
                                "renewable_generators",
                                v.id,
                                "availableResource"
                              ),
                            },
                            {
                              name: "Annual Sales ($-yr)",
                              value: this.getAnnualValue("renewable_generators", v.id, "sales"),
                            },
                            {
                              name: "Annual P&L ($-yr)",
                              value: this.getAnnualValue("renewable_generators", v.id, "pL"),
                            },
                          ],
                        },
                      },
                      {
                        title: "P&L ($)",
                        component: Plotly,
                        props: {
                          toImageButtonOptions: this.chartDownloadName(
                            this.caseName,
                            "SSE",
                            "P&L ($)"
                          ),
                          layout: {
                            images: [this.acelerexLogo],
                            yaxis: {
                              title: "P&L [$]",
                            },
                          },
                          data: this.getResourceCompChartData(
                            v.id,
                            "renewable_generators",
                            "storages",
                            "pL"
                          ),
                        },
                      },
                    ],
                  },
                },
              },
            ],
          })),
          monthly: Object.entries(this.SSEMonthlyData.renewable_generators || {}).map(
            ([key, v], i) => ({
              title: key,
              children: [
                {
                  title: `renGen-${i}`,
                  component: OutputSection,
                  props: {
                    section: {
                      title: `renGen-${i}-items`,
                      children: [
                        {
                          title: "Monthly Generation (MWh)",
                          component: Plotly,
                          props: {
                            layout: {
                              images: [this.acelerexLogo],
                              yaxis: {
                                title: "Generation [MWh]",
                                fixedrange: true,
                              },
                              xaxis: {
                                tickformat:
                                  this.SSEToolbarSettings.selectedYear.length >= 2 ? "%m" : "",
                              },
                            },
                            data: this.SSEToolbarSettings.selectedYear.length
                              ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                  name: year,
                                  ...this.getChartData(v, "totalGeneration", {
                                    dataType: "monthly",
                                    selectedYear: year,
                                  }),
                                  type: "bar",
                                }))
                              : [
                                  {
                                    ...this.getChartData(v, "totalGeneration", {
                                      dataType: "monthly",
                                      selectedYear: Object.keys(v)[0],
                                    }),
                                    type: "bar",
                                  },
                                ],
                          },
                        },
                      ],
                    },
                  },
                },
              ],
            })
          ),
          daily: Object.entries(this.SSEDailyData.renewable_generators || {}).map(
            ([key, v], i) => ({
              title: key,
              children: [
                {
                  title: `renGen-${i}`,
                  component: OutputSection,
                  props: {
                    section: {
                      title: `renGen-${i}-items`,
                      children: [
                        {
                          title: "Daily Generation (MWh)",
                          component: Plotly,
                          props: {
                            layout: {
                              images: [this.acelerexLogo],
                              yaxis: {
                                title: "Generation [MWh]",
                                fixedrange: true,
                              },
                              xaxis: {
                                tickformat:
                                  this.SSEToolbarSettings.selectedYear.length >= 2 ? "%d/%m" : "",
                              },
                            },
                            data: this.SSEToolbarSettings.selectedYear.length
                              ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                  name: year,
                                  ...this.getChartData(v, "totalGeneration", {
                                    dataType: "daily",
                                    selectedYear: year,
                                  }),
                                  type: "bar",
                                }))
                              : [
                                  {
                                    ...this.getChartData(v, "totalGeneration", {
                                      dataType: "daily",
                                      selectedYear: Object.keys(v)[0],
                                    }),
                                    type: "bar",
                                  },
                                ],
                          },
                        },
                      ],
                    },
                  },
                },
              ],
            })
          ),
          timeSeries: Object.entries(this.SSETimeseriesData.renewable_generators || {}).map(
            ([key, v], i) => ({
              title: key,
              children: [
                {
                  title: `renGen-${i}`,
                  component: OutputSection,
                  props: {
                    panel: [0],
                    section: {
                      title: `renGen-${i}-items`,
                      children: [
                        {
                          title: "Generation (MWh)",
                          component: ChartTimeseries,
                          props: {
                            layout: {
                              yaxis: {
                                title: "Generation [MWh]",
                              },
                              yaxis2: {
                                title: "[$/MWh]",
                                overlaying: "y",
                                side: "right",
                              },
                            },
                            data: [
                              {
                                name: "Generation [MWh]",
                                ...this.getChartData(v, "generation", {}),
                                type: "bar",
                              },
                              {
                                name: "Energy Price [$/MWh]",
                                ...this.getChartData(v, "energyPrice", { duplicateLast: true }),
                                yaxis: "y2",
                                mode: "lines",
                                line: {
                                  shape: "hvh",
                                },
                              },
                              {
                                name: "SRMC [$/MWh]",
                                ...this.getChartData(v, "srmc", { duplicateLast: true }),
                                yaxis: "y2",
                                mode: "lines",
                                line: {
                                  shape: "hvh",
                                },
                              },
                            ],
                          },
                        },
                        {
                          title: "P&L ($)",
                          component: ChartTimeseries,
                          props: {
                            layout: {
                              yaxis: {
                                title: "P&L [MWh]",
                              },
                              yaxis2: {
                                title: "[$/MWh]",
                                overlaying: "y",
                                side: "right",
                              },
                            },
                            data: [
                              {
                                name: "P&L [$]",
                                ...this.getChartData(v, "pL", {}),
                                type: "bar",
                              },
                              {
                                name: "Energy Price [$/MWh]",
                                ...this.getChartData(v, "energyPrice", { duplicateLast: true }),
                                yaxis: "y2",
                                mode: "lines",
                                line: {
                                  shape: "hvh",
                                },
                              },
                            ],
                          },
                        },
                      ],
                    },
                  },
                },
              ],
            })
          ),
        };
        page.sections.push({
          title: "Renewable Generators",
          panel: this.SSEResourcesItems.renewable_generators.map((el, i) => i),
          children: sectionChildren[this.SSEToolbarSettings.viewMode] || [],
        });
      }

      if (
        this.SSEToolbarSettings.demands &&
        this.SSEToolbarSettings.demands.length &&
        this.SSEDashboardToolbarSettings.viewMode === "single"
      ) {
        const { viewMode } = this.SSEToolbarSettings;
        let panelChildren;
        if (viewMode === "annual") {
          panelChildren = Object.values(
            this.SSEYearlyData[Object.keys(this.SSEYearlyData)[0]].demands
          ).map((v) => ({
            title: `${v.name}`,
            children: [
              {
                title: `demand-${v.id}`,
                component: OutputSection,
                props: {
                  panel: [0],
                  section: {
                    title: `demand-${v.id}-items`,
                    children: [
                      {
                        title: "Summary",
                        component: DataTableYearly,
                        props: {
                          year_start: this.SSEStartYear,
                          items: [
                            {
                              name: "Total Demand (MWh-yr)",
                              value: this.getAnnualValue("demands", v.id, "totalDemand"),
                            },
                            {
                              name: "Served Demand (MWh-yr)",
                              value: this.getAnnualValue("demands", v.id, "servedDemand"),
                            },
                            {
                              name: "Lost Load (MWh-yr)",
                              value: this.getAnnualValue("demands", v.id, "lostLoad"),
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              },
            ],
          }));
        } else if (viewMode === "monthly") {
          panelChildren = Object.entries(this.SSEMonthlyData.demands || {}).map(([key, v], i) => ({
            title: key,
            children: [
              {
                title: `demand-${i}`,
                component: OutputSection,
                props: {
                  section: {
                    title: `demand-${i}-items`,
                    children: [
                      {
                        title: "Monthly Total Demand (MWh)",
                        component: Plotly,
                        props: {
                          layout: {
                            images: [this.acelerexLogo],
                            yaxis: {
                              title: "Demand [MWh]",
                              fixedrange: true,
                            },
                            xaxis: {
                              tickformat:
                                this.SSEToolbarSettings.selectedYear.length >= 2 ? "%m" : "",
                            },
                          },
                          data: this.SSEToolbarSettings.selectedYear.length
                            ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                name: year,
                                ...this.getChartData(v, "totalDemand", {
                                  dataType: "monthly",
                                  selectedYear: year,
                                }),
                                type: "bar",
                              }))
                            : [
                                {
                                  ...this.getChartData(v, "totalDemand", {
                                    dataType: "monthly",
                                    selectedYear: Object.keys(v)[0],
                                  }),
                                  type: "bar",
                                },
                              ],
                        },
                      },
                    ],
                  },
                },
              },
            ],
          }));
        } else if (viewMode === "daily") {
          panelChildren = Object.entries(this.SSEDailyData.demands || {}).map(([key, v], i) => ({
            title: key,
            children: [
              {
                title: `demand-${i}`,
                component: OutputSection,
                props: {
                  section: {
                    title: `demand-${i}-items`,
                    children: [
                      {
                        title: "Daily Total Demand (MWh)",
                        component: Plotly,
                        props: {
                          layout: {
                            images: [this.acelerexLogo],
                            yaxis: {
                              title: "Demand [MWh]",
                              fixedrange: true,
                            },
                            xaxis: {
                              tickformat:
                                this.SSEToolbarSettings.selectedYear.length >= 2 ? "%d/%m" : "",
                            },
                          },
                          data: this.SSEToolbarSettings.selectedYear.length
                            ? this.SSEToolbarSettings.selectedYear.map((year) => ({
                                name: year,
                                ...this.getChartData(v, "totalDemand", {
                                  dataType: "daily",
                                  selectedYear: year,
                                }),
                                type: "bar",
                              }))
                            : [
                                {
                                  ...this.getChartData(v, "totalDemand", {
                                    dataType: "daily",
                                    selectedYear: Object.keys(v)[0],
                                  }),
                                  type: "bar",
                                },
                              ],
                        },
                      },
                    ],
                  },
                },
              },
            ],
          }));
        } else if (viewMode === "timeSeries") {
          panelChildren = this.SSETimeseriesData.demands
            ? Object.entries(this.SSETimeseriesData.demands).map(([key, v], i) => ({
                title: key,
                children: [
                  {
                    title: `demand-view-${i}`,
                    component: OutputSection,
                    props: {
                      panel: [1],
                      section: {
                        title: "items",
                        children: [
                          {
                            title: "Lost Load & Energy Price",
                            component: ChartTimeseries,
                            props: {
                              toImageButtonOptions: this.chartDownloadName(
                                this.caseName,
                                "SSE",
                                "Lost Load & Energy Price"
                              ),
                              layout: {
                                barmode: "relative",
                                yaxis: {
                                  title: "MW",
                                },
                                yaxis2: {
                                  title: "$/MWh",
                                  overlaying: "y",
                                  side: "right",
                                },
                              },
                              data: [
                                {
                                  name: "Lost Load [MW]",
                                  ...this.getChartData(v, "lostLoad"),
                                  type: "scatter",
                                  yaxis: "y2",
                                },
                                {
                                  name: "Energy Price [$/MWh]",
                                  ...this.getChartData(v, "energyPrice"),
                                  mode: "lines",
                                  yaxis: "y2",
                                  line: {
                                    shape: "hvh",
                                  },
                                },
                              ],
                            },
                          },
                          {
                            title: "Demand & Energy Price",
                            component: ChartTimeseries,
                            props: {
                              toImageButtonOptions: this.chartDownloadName(
                                this.caseName,
                                "SSE",
                                "Demand & Energy Price"
                              ),
                              layout: {
                                barmode: "relative",
                                yaxis: {
                                  title: "[MWh]",
                                },
                                yaxis2: {
                                  title: "[$/MWh]",
                                  overlaying: "y",
                                  side: "right",
                                },
                              },
                              data: [
                                {
                                  name: "Demand [MWh]",
                                  ...this.getChartData(v, "demand"),
                                  type: "bar",
                                },
                                {
                                  name: "Energy Price [$/MWh]",
                                  ...this.getChartData(v, "energyPrice"),
                                  mode: "lines",
                                  yaxis: "y2",
                                  line: {
                                    shape: "hvh",
                                  },
                                },
                              ],
                            },
                          },
                        ],
                      },
                    },
                  },
                ],
              }))
            : [];
        }
        page.sections.push({
          title: "Demands",
          panel: this.SSEResourcesItems.demands.map((el, i) => i),
          children: panelChildren,
        });
      }
      return page;
    },
    SSEActiveServices() {
      return Object.entries(
        this.currentCase.input.projects.filter((p) => p.active)[0].storages[0].services
      )
        .filter((entry) => entry[1].active)
        .map((entry) => this.mapServiceToOutputKey(entry[0]));
    },
    SSEFilters() {
      return this.SSEOutput.props;
    },
    SettedSSEToolbar() {
      return Object.keys(this.SSEToolbarSettings).includes("dates");
    },
    SSEStartYear() {
      const startYear = Object.keys(this.SSEYearlyData)[0];
      return Number(startYear);
    },
    SSEStartDate() {
      return this.SSEDates[0] ? this.SSEDates[0] : "";
    },
    SSEEndDate() {
      return this.SSEDates[1] ? this.SSEDates[1] : "";
    },
    SSEDailyRecords() {
      return this.SSEToolbarSettings.viewMode !== "daily"
        ? 1440 / this.currentCase.settings.timeline_settings.time_interval
        : 1440;
    },
    SSENumberOfDays() {
      return (
        DateTime.fromISO(this.SSEEndDate, {
          zone: "utc",
        })
          .diff(
            DateTime.fromISO(this.SSEStartDate, {
              zone: "utc",
            }),
            "days"
          )
          .toObject().days || 1
      );
    },
  },
  watch: {
    output(val) {
      if (!val || !val.STACKED_SERVICES_EMULATOR) return;
      this.initSSEOutputs();
      this.SSEToolbarSettings.hasDaily = true;
      this.SSEToolbarSettings.hasMonthly = true;
    },
    settings(val) {
      if (val) {
        this.SSEToolbarSettings.timeInterval = val.timeline_settings.time_interval;
        this.SSEDates = Array(2).fill(DateTime.fromISO(`${val.year_start}-01-01`).toISODate());
      }
    },
    "SSEToolbarSettings.storages": function () {
      this.updateSSEData();
    },
    "SSEToolbarSettings.thermal_generators": function () {
      this.updateSSEData();
    },
    "SSEToolbarSettings.renewable_generators": function () {
      this.updateSSEData();
    },
    "SSEToolbarSettings.demands": function () {
      this.updateSSEData();
    },
    "SSEToolbarSettings.markets": function () {
      this.updateSSEData();
    },
    "SSEToolbarSettings.viewMode": function () {
      this.updateSSEData();
    },
    "SSEToolbarSettings.selectedYear": function () {
      this.updateSSEData();
    },
    SSEDates() {
      this.updateSSEData();
    },
    "SSEToolbarSettings.project": function () {
      this.getResourcesForToolbar().then(() => {
        this.updateSSEData();
      });
    },
  },
  beforeDestroy() {
    // Clean up database connection to prevent memory leaks
    if (this.dbSql) {
      this.dbSql.close();
      this.dbSql = null;
    }
    // Clear project metadata
    this.sqlSSEProjectList = [];
  },
};
